/**
 * Governance API client — admin-protected tenant/identity lifecycle.
 */

import { governanceHeadersJson as headersJson } from "./governance-http-headers.js";

const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ??
  "/api/v1";

/**
 * Low-level governance fetch helper returning the raw `Response`.
 *
 * Resolves a path against `API_BASE` (absolute URLs are passed through) and
 * merges the admin governance headers with any per-call headers. Callers are
 * responsible for inspecting `res.ok` and parsing the body.
 */
export async function governanceApiFetch(
  path: string,
  init: RequestInit = {}
): Promise<Response> {
  const url = /^https?:\/\//.test(path)
    ? path
    : `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;
  return fetch(url, {
    ...init,
    headers: { ...headersJson(), ...(init.headers ?? {}) },
  });
}

export interface GovernanceTenantRow {
  id: number;
  externalId: string;
  name: string;
  domain: string;
  plan: string;
  settings: Record<string, unknown>;
  /** Optional residency label (Phase C.8). */
  dataRegion?: string | null;
  /** Present when gateway env `PLATFORM_DATA_REGION` is set (deployment region hint). */
  platformDataRegion?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export async function fetchGovernanceTenants(): Promise<{
  items: GovernanceTenantRow[];
  total: number;
}> {
  const res = await fetch(`${API_BASE}/tenants?limit=200`, {
    headers: headersJson(),
  });
  if (!res.ok) {
    throw new Error(`tenants failed: ${res.status}`);
  }
  return res.json() as Promise<{ items: GovernanceTenantRow[]; total: number }>;
}

export async function createGovernanceTenant(body: {
  name: string;
  domain: string;
  plan?: "smb" | "enterprise";
  settings?: Record<string, unknown>;
}): Promise<GovernanceTenantRow & { bootstrapToken?: string }> {
  const res = await fetch(`${API_BASE}/tenants`, {
    method: "POST",
    headers: headersJson(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `create tenant failed: ${res.status}`);
  }
  return res.json() as Promise<GovernanceTenantRow & { bootstrapToken?: string }>;
}

export interface GovernanceIdentityDetail {
  externalId: string;
  identifier: string;
  displayName: string;
  type: string;
  status: string;
  metadata: Record<string, unknown>;
  lastSeenAt: string | null;
  createdAt: string;
  updatedAt: string;
  idpMappings?: Array<{ provider: string; idpSubject: string }>;
  idpSubject?: string | null;
}

export type SessionCaptureIdpProvider =
  | "okta"
  | "entra"
  | "aws_iam"
  | "aws_identity_center"
  | "gcp_iam"
  | "oidc"
  | "auth0"
  | "jumpcloud"
  | "ad";

export interface SessionCaptureConfig {
  enabled: boolean;
  defaultScopes: string[];
  defaultProvider?: SessionCaptureIdpProvider;
  deriveFromRequest: boolean;
  allowIdentifierLookup: boolean;
  /** Auto-provision observe-only identities for unmapped IdP subjects on webhook capture. */
  autoProvisionUnmappedSubjects?: boolean;
  devicePostureDefaults: {
    osVersion: string;
    patchLevel: string;
    encryptionEnabled: boolean;
    firewallEnabled: boolean;
    antivirusActive: boolean;
    complianceScore: number;
  };
  geoDefaults: {
    country: string;
    region: string;
    city: string;
    riskScore: number;
  };
  autoCapture?: {
    enabled: boolean;
    onlyInteractive?: boolean;
    cooldownSeconds?: number;
    providers?: Partial<
      Record<
        SessionCaptureIdpProvider,
        { connectionExternalId?: string; appIds: string[] }
      >
    >;
  };
  /** @deprecated Legacy — still returned on GET for older tenants. */
  entraAutoCapture?: {
    enabled: boolean;
    appIds: string[];
    onlyInteractive?: boolean;
    cooldownSeconds?: number;
  };
}

export type SignInWebhookStatus = "not_configured" | "active" | "expired" | "error";

export interface SignInWebhookStatusResponse {
  status: SignInWebhookStatus;
  subscription: {
    id: string;
    expirationDateTime: string;
    resource: string;
    notificationUrl: string;
    status: string;
    updatedAt: string;
    lastError?: string;
  } | null;
  notificationUrl: string | null;
}

export interface SessionCaptureStatusResponse {
  tenantExternalId: string;
  sessionCaptureEnabled: boolean;
  autoCaptureEnabled: boolean;
  providers: Array<{
    provider: string;
    connectionExternalId: string | null;
    webhookStatus: string;
    appIds: string[];
    lastWebhookReceivedAt: string | null;
    lastSignInReceivedAt: string | null;
    lastSessionCreatedAt: string | null;
    lastSystemLog: {
      received: number;
      accepted: number;
      duplicates: number;
      failures: number;
      recordedAt: string;
    } | null;
    lastEventTypes: {
      eventTypes: Record<string, number>;
      missingUserId: number;
      sampleRejected: { type: string | null; hasUserId: boolean; client_id?: string } | null;
      recordedAt: string;
    } | null;
    recentErrors: Array<{ at: string; status: number; error: string }>;
    captureOutcomes?: Record<string, number>;
  }>;
}

export interface CaptureDiagnosticCheck {
  id: string;
  label: string;
  status: "pass" | "fail" | "warn" | "idle";
  detail?: string;
}

export interface CaptureDiagnosticResult {
  tenantExternalId: string;
  provider: string;
  connectionExternalId: string | null;
  checks: CaptureDiagnosticCheck[];
  summary: { status: "pass" | "fail" | "warn" | "idle"; label: string };
  markers: {
    lastWebhookReceivedAt: string | null;
    lastSignInReceivedAt: string | null;
    lastSessionCreatedAt: string | null;
    lastSystemLog: {
      received: number;
      accepted: number;
      duplicates: number;
      failures: number;
      recordedAt: string;
    } | null;
    lastEventTypes: {
      eventTypes: Record<string, number>;
      missingUserId: number;
      sampleRejected: { type: string | null; hasUserId: boolean; client_id?: string } | null;
      recordedAt: string;
    } | null;
  };
  recentErrors: Array<{ at: string; status: number; error: string }>;
  captureOutcomes: Record<string, number>;
  captureCooldownSeconds?: number;
  dlqEntries: Array<{ id: string; source: string; errorReason: string; createdAt: string }>;
}

export interface CaptureTestResult {
  mode: "dry-run" | "live";
  provider: string;
  injected?: boolean;
  subject?: string;
  appId?: string | null;
  requestId?: string;
  connectionExternalId?: string | null;
  error?: string;
  wouldProceed: boolean;
  stoppedAt: string | null;
  autoProvisionEnabled?: boolean;
  mapping: { identityExternalId: string } | null;
  checks: CaptureDiagnosticCheck[];
  hint?: string;
}

export interface SessionCaptureConfigResponse {
  tenantExternalId: string;
  source: "tenant" | "default";
  config: SessionCaptureConfig;
}

export async function fetchSessionCaptureConfig(
  tenantExternalId: string
): Promise<SessionCaptureConfigResponse> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/session-capture`
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `session-capture config failed: ${res.status}`);
  }
  return res.json() as Promise<SessionCaptureConfigResponse>;
}

export async function fetchSessionCaptureStatus(
  tenantExternalId: string
): Promise<SessionCaptureStatusResponse> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/session-capture/status`
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `session-capture status failed: ${res.status}`);
  }
  return res.json() as Promise<SessionCaptureStatusResponse>;
}

export async function fetchSessionCaptureDiagnose(
  tenantExternalId: string,
  opts?: { provider?: string; connectionExternalId?: string }
): Promise<CaptureDiagnosticResult> {
  const q = new URLSearchParams();
  if (opts?.provider) q.set("provider", opts.provider);
  if (opts?.connectionExternalId) q.set("connectionExternalId", opts.connectionExternalId);
  const qs = q.toString() ? `?${q.toString()}` : "";
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/session-capture/diagnose${qs}`
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `session-capture diagnose failed: ${res.status}`);
  }
  return res.json() as Promise<CaptureDiagnosticResult>;
}

export async function sendSessionCaptureTestEvent(
  tenantExternalId: string,
  body: {
    provider?: string;
    connectionExternalId?: string;
    subject?: string;
    appId?: string;
    dryRun?: boolean;
    ipAddress?: string;
    userAgent?: string;
  }
): Promise<CaptureTestResult> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/session-capture/test`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `session-capture test failed: ${res.status}`);
  }
  return res.json() as Promise<CaptureTestResult>;
}

export async function saveSessionCaptureConfig(
  tenantExternalId: string,
  config: SessionCaptureConfig
): Promise<SessionCaptureConfigResponse> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/session-capture`,
    {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(config),
    }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new Error(err.message ?? err.error ?? `save session-capture failed: ${res.status}`);
  }
  return res.json() as Promise<SessionCaptureConfigResponse>;
}

export type GovernanceIdentityTimelineEvent = {
  ts: string;
  kind: string;
  summary: string;
  sessionId?: string | null;
  data?: Record<string, unknown>;
};

export async function fetchGovernanceIdentityTimeline(
  tenantExternalId: string,
  identityExternalId: string,
  params?: { limit?: number; offset?: number }
): Promise<{
  items: GovernanceIdentityTimelineEvent[];
  limit: number;
  offset: number;
  hasMore: boolean;
}> {
  const qs = new URLSearchParams();
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  const q = qs.toString();
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/identities/${encodeURIComponent(identityExternalId)}/timeline${q ? `?${q}` : ""}`,
    { headers: headersJson() }
  );
  if (!res.ok) {
    throw new Error(`timeline failed: ${res.status}`);
  }
  return res.json() as Promise<{
    items: GovernanceIdentityTimelineEvent[];
    limit: number;
    offset: number;
    hasMore: boolean;
  }>;
}

export type GovernanceBehavioralBaseline = {
  baselineType: string;
  accessFrequencyEma: number;
  dataVolumeEma: number;
  sampleCount: number;
  lastUpdatedAt: string;
};

export type GovernanceBehaviorTelemetrySummary = {
  windowDays: number;
  total: number;
  averageConfidenceScore: number;
  actionBreakdown: { allow: number; challenge: number; revoke: number };
  riskBandBreakdown: { low: number; medium: number; high: number; critical: number };
  averageAnomalyHealth: number;
  averageBehavioralHealth: number;
};

export type GovernanceRecentEvaluation = {
  sessionId: string;
  confidenceScore: number;
  action: string;
  riskStatus: string;
  anomalyScore: number;
  behavioralScore: number;
  createdAt: string;
};

export interface GovernanceBehaviorProfileResponse {
  identityExternalId: string;
  baselines: GovernanceBehavioralBaseline[];
  telemetrySummary?: GovernanceBehaviorTelemetrySummary | null;
  recentEvaluations?: GovernanceRecentEvaluation[];
}

export async function fetchGovernanceIdentityBehaviorProfile(
  tenantExternalId: string,
  identityExternalId: string
): Promise<GovernanceBehaviorProfileResponse> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/identities/${encodeURIComponent(identityExternalId)}/behavior-profile`,
    { headers: headersJson() }
  );
  if (!res.ok) {
    throw new Error(`behavior profile failed: ${res.status}`);
  }
  return res.json() as Promise<GovernanceBehaviorProfileResponse>;
}

// ─── Sprint 5B — Access reviews (GOV-06/07/08) ───────────────────────────────

export type AccessReviewCampaignStatus =
  | "draft"
  | "active"
  | "completed"
  | "cancelled";

export interface AccessReviewCampaignSummary {
  id: number;
  tenantId: number;
  externalId: string;
  name: string;
  description: string | null;
  status: AccessReviewCampaignStatus;
  dueAt: string | null;
  /** Sprint S6: when the lifecycle worker escalated this overdue campaign. */
  escalatedAt: string | null;
  /** Sprint S6: the manager identity the overdue campaign was escalated to. */
  escalatedToIdentityId: number | null;
  reviewerAdminUserId: number;
  createdAt: string;
  updatedAt: string;
  itemCounts: {
    total: number;
    pending: number;
    approved: number;
    denied: number;
  };
}

export interface AccessReviewItemWithIdentity {
  id: number;
  tenantId: number;
  campaignId: number;
  identityId: number;
  identityExternalId: string;
  identityDisplayName: string;
  identityIdentifier: string;
  identityType: string;
  status: "pending" | "approved" | "denied";
  /** Sprint S6: per-item due date; null falls back to the campaign dueAt. */
  dueAt: string | null;
  lastDecisionAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function fetchAccessReviewCampaigns(
  tenantExternalId: string,
  params?: { limit?: number; offset?: number; status?: AccessReviewCampaignStatus }
): Promise<{
  items: AccessReviewCampaignSummary[];
  total: number;
  limit: number;
  offset: number;
}> {
  const q = new URLSearchParams();
  if (params?.limit != null) q.set("limit", String(params.limit));
  if (params?.offset != null) q.set("offset", String(params.offset));
  if (params?.status) q.set("status", params.status);
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/access-reviews/campaigns?${q.toString()}`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`access review campaigns failed: ${res.status}`);
  return res.json() as Promise<{ items: AccessReviewCampaignSummary[]; total: number; limit: number; offset: number }>;
}

export async function fetchAccessReviewCampaign(
  tenantExternalId: string,
  campaignExternalId: string
): Promise<AccessReviewCampaignSummary> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/access-reviews/campaigns/${encodeURIComponent(campaignExternalId)}`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`access review campaign failed: ${res.status}`);
  return res.json() as Promise<AccessReviewCampaignSummary>;
}

/** Sprint S6 — active campaigns past their due date (overdue/escalated badges). */
export async function fetchOverdueAccessReviewCampaigns(
  tenantExternalId: string
): Promise<{ items: AccessReviewCampaignSummary[]; total: number }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/access-reviews/campaigns/overdue`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`overdue access review campaigns failed: ${res.status}`);
  return res.json() as Promise<{ items: AccessReviewCampaignSummary[]; total: number }>;
}

export async function createAccessReviewCampaign(
  tenantExternalId: string,
  body: {
    name: string;
    description?: string | null;
    dueAt?: string | null;
    reviewerAdminUserId: number;
    identityExternalIds?: string[];
  }
): Promise<AccessReviewCampaignSummary> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/access-reviews/campaigns`,
    {
      method: "POST",
      headers: headersJson(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new Error(err.message ?? err.error ?? `create access review failed: ${res.status}`);
  }
  return res.json() as Promise<AccessReviewCampaignSummary>;
}

export async function patchAccessReviewCampaign(
  tenantExternalId: string,
  campaignExternalId: string,
  body: {
    status?: AccessReviewCampaignStatus;
    name?: string;
    description?: string | null;
    dueAt?: string | null;
  }
): Promise<AccessReviewCampaignSummary> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/access-reviews/campaigns/${encodeURIComponent(campaignExternalId)}`,
    {
      method: "PATCH",
      headers: headersJson(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new Error(err.message ?? err.error ?? `patch access review campaign failed: ${res.status}`);
  }
  return res.json() as Promise<AccessReviewCampaignSummary>;
}

export async function addAccessReviewCampaignItems(
  tenantExternalId: string,
  campaignExternalId: string,
  identityExternalIds: string[]
): Promise<{ inserted: number }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/access-reviews/campaigns/${encodeURIComponent(campaignExternalId)}/items`,
    {
      method: "POST",
      headers: headersJson(),
      body: JSON.stringify({ identityExternalIds }),
    }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new Error(err.message ?? err.error ?? `add access review items failed: ${res.status}`);
  }
  return res.json() as Promise<{ inserted: number }>;
}

export async function fetchAccessReviewItems(
  tenantExternalId: string,
  campaignExternalId: string,
  params?: { limit?: number; offset?: number; status?: "pending" | "approved" | "denied" }
): Promise<{
  campaign: AccessReviewCampaignSummary;
  items: AccessReviewItemWithIdentity[];
  total: number;
  limit: number;
  offset: number;
}> {
  const q = new URLSearchParams();
  if (params?.limit != null) q.set("limit", String(params.limit));
  if (params?.offset != null) q.set("offset", String(params.offset));
  if (params?.status) q.set("status", params.status);
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/access-reviews/campaigns/${encodeURIComponent(campaignExternalId)}/items?${q.toString()}`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`access review items failed: ${res.status}`);
  return res.json() as Promise<{
    campaign: AccessReviewCampaignSummary;
    items: AccessReviewItemWithIdentity[];
    total: number;
    limit: number;
    offset: number;
  }>;
}

export async function submitAccessReviewDecision(
  tenantExternalId: string,
  campaignExternalId: string,
  identityExternalId: string,
  body: { decision: "approve" | "deny"; comment?: string | null }
): Promise<unknown> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/access-reviews/campaigns/${encodeURIComponent(campaignExternalId)}/items/${encodeURIComponent(identityExternalId)}/decisions`,
    {
      method: "POST",
      headers: headersJson(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new Error(err.message ?? err.error ?? `decision failed: ${res.status}`);
  }
  return res.json() as Promise<unknown>;
}

export async function fetchGovernanceIdentity(
  tenantExternalId: string,
  identityExternalId: string
): Promise<GovernanceIdentityDetail> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/identities/${encodeURIComponent(identityExternalId)}`,
    { headers: headersJson() }
  );
  if (!res.ok) {
    throw new Error(`identity get failed: ${res.status}`);
  }
  return res.json() as Promise<GovernanceIdentityDetail>;
}

export interface GovernanceIdentitySessionRow {
  sessionId: string;
  startedAt: string;
  expiresAt: string;
  currentConfidenceScore: number;
  lastAction: string;
  ipAddress: string | null;
  country: string | null;
  identityType: string;
  // History fields — present when the route is asked for recent sessions
  // (active + terminated). Optional so existing call-sites keep working.
  isActive?: boolean;
  terminatedAt?: string | null;
  terminationReason?: string | null;
  lastEvaluatedAt?: string | null;
}

export type ResolveIdpProvider =
  | "okta"
  | "entra"
  | "aws_iam"
  | "aws_identity_center"
  | "gcp_iam"
  | "oidc"
  | "auth0"
  | "jumpcloud"
  | "ad";

export interface ResolveIdpIdentityResult {
  provider: ResolveIdpProvider;
  idpSubject: string;
  identityExternalId: string;
  identifier: string;
  displayName: string;
  status: string;
}

/** Map Entra oid (or other IdP subject) → IA external ID for auth/request. */
export async function fetchResolveIdpIdentity(
  tenantExternalId: string,
  params: { provider: ResolveIdpProvider; idpSubject: string }
): Promise<ResolveIdpIdentityResult> {
  const q = new URLSearchParams({
    provider: params.provider,
    idpSubject: params.idpSubject,
  });
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/identities/resolve-idp?${q.toString()}`,
    { headers: headersJson() }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? `resolve-idp failed: ${res.status}`);
  }
  return res.json() as Promise<ResolveIdpIdentityResult>;
}

export async function fetchGovernanceIdentitySessions(
  tenantExternalId: string,
  identityExternalId: string,
  opts?: { history?: number }
): Promise<{ items: GovernanceIdentitySessionRow[] }> {
  const qs = opts?.history && opts.history > 0
    ? `?history=${Math.min(Math.max(opts.history, 1), 50)}`
    : "";
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/identities/${encodeURIComponent(identityExternalId)}/sessions${qs}`,
    { headers: headersJson() }
  );
  if (!res.ok) {
    throw new Error(`identity sessions failed: ${res.status}`);
  }
  return res.json() as Promise<{ items: GovernanceIdentitySessionRow[] }>;
}

export interface GovernanceSecurityInsightsPayload {
  policy: {
    externalId: string;
    name: string;
    minConfidenceScore: number;
    maxTokenTTLSeconds: number;
  } | null;
  baselines: Array<{
    baselineType: string;
    accessFrequencyEma: number;
    dataVolumeEma: number;
    sampleCount: number;
    lastUpdatedAt: string;
  }>;
  elevations: {
    total: number;
    items: Array<{
      externalId: string;
      status: string;
      requestedScopes: string[];
      grantedScopes: string[];
      expiresAt: string | null;
      approvedAt: string | null;
      sessionId: string | null;
      createdAt: string;
      policyRuleMatched?: string | null;
      approvalDecision?: string | null;
      requiredApprovers?: number;
      currentApprovers?: number;
      isBreakGlass?: boolean;
      revokedBy?: string | null;
      revokeReason?: string | null;
    }>;
  };
  sessionCompliance: {
    lowestActiveConfidence: number | null;
    meetsPolicyFloor: boolean | null;
  };
}

export async function fetchGovernanceIdentitySecurityInsights(
  tenantExternalId: string,
  identityExternalId: string
): Promise<GovernanceSecurityInsightsPayload> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/identities/${encodeURIComponent(identityExternalId)}/security-insights`,
    { headers: headersJson() }
  );
  if (!res.ok) {
    throw new Error(`identity security insights failed: ${res.status}`);
  }
  return res.json() as Promise<GovernanceSecurityInsightsPayload>;
}

/** GDPR retention / processing notes from `tenant.settings` (Phase B.5). */
export interface GdprPolicyPayload {
  tenantExternalId: string;
  gdpr: {
    dataRetentionDays?: number;
    processingNotes?: string;
  };
}

export async function fetchGdprPolicy(tenantExternalId: string): Promise<GdprPolicyPayload> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/gdpr/policy`,
    { headers: headersJson() }
  );
  if (!res.ok) {
    throw new Error(`gdpr policy failed: ${res.status}`);
  }
  return res.json() as Promise<GdprPolicyPayload>;
}

/**
 * Tenant-scoped JSON export (identities + policies snapshot). Audited server-side.
 */
export async function downloadTenantDataExportJson(tenantExternalId: string): Promise<void> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/data-export`,
    { headers: headersJson() }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `data export failed: ${res.status}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `tenant-data-export-${tenantExternalId}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * GDPR right-to-erasure: anonymize identifier and revoke identity (Phase B.5).
 * Emits audit + `identity.gdpr_anonymized` webhook when configured.
 */
export async function gdprAnonymizeIdentity(
  tenantExternalId: string,
  identityExternalId: string
): Promise<GovernanceIdentityDetail> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/identities/${encodeURIComponent(identityExternalId)}/gdpr-anonymize`,
    { method: "POST", headers: headersJson() }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `gdpr anonymize failed: ${res.status}`);
  }
  return res.json() as Promise<GovernanceIdentityDetail>;
}

export async function suspendGovernanceIdentity(
  tenantExternalId: string,
  identityExternalId: string
): Promise<GovernanceIdentityDetail> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/identities/${encodeURIComponent(identityExternalId)}/suspend`,
    { method: "POST", headers: headersJson() }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `suspend failed: ${res.status}`);
  }
  return res.json() as Promise<GovernanceIdentityDetail>;
}

export async function quarantineGovernanceIdentity(
  tenantExternalId: string,
  identityExternalId: string
): Promise<GovernanceIdentityDetail> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/identities/${encodeURIComponent(identityExternalId)}/quarantine`,
    { method: "POST", headers: headersJson() }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `quarantine failed: ${res.status}`);
  }
  return res.json() as Promise<GovernanceIdentityDetail>;
}

// ─── RFC-004 R3.5 — identity merge (duplicate → canonical) ───────────────────

export type GovernanceIdentityMergeResult = {
  dryRun: boolean;
  duplicateExternalId: string;
  canonicalExternalId: string;
  mappingsRelinked: number;
  managerRefsRelinked: number;
  duplicateStatusAfter: string;
  preserved: string[];
  duplicateActiveSessions: number | null;
};

/**
 * Merge a duplicate identity into a canonical one. The endpoint defaults to a
 * dry-run: omit `dryRun` to preview, send `dryRun: false` to execute. This is
 * a destructive security operation — callers must run the dry-run first and
 * require an explicit second action to execute.
 */
export async function mergeGovernanceIdentity(
  tenantExternalId: string,
  duplicateExternalId: string,
  body: { canonicalExternalId: string; dryRun?: boolean; actor?: string }
): Promise<GovernanceIdentityMergeResult> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/identities/${encodeURIComponent(duplicateExternalId)}/merge`,
    {
      method: "POST",
      headers: headersJson(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `identity merge failed: ${res.status}`);
  }
  return res.json() as Promise<GovernanceIdentityMergeResult>;
}

export type IdentityReenableRequest = {
  id: number;
  externalId: string;
  tenantId: number;
  identityId: number;
  status: "pending" | "approved" | "rejected";
  justification: string | null;
  requestedByAdminUserId: number;
  approvalsCount: number;
  approverAdminUserIds: number[];
  createdAt: string;
  updatedAt: string;
};

function parseReenableRequest(input: unknown): IdentityReenableRequest {
  const r = input as Record<string, unknown> & {
    id: number;
    externalId: string;
    tenantId: number;
    identityId: number;
    status: IdentityReenableRequest["status"];
    justification?: string | null;
    requestedByAdminUserId: number;
    approvalsCount?: number;
    createdAt: string | number | Date;
    updatedAt: string | number | Date;
  };
  return {
    id: r.id,
    externalId: r.externalId,
    tenantId: r.tenantId,
    identityId: r.identityId,
    status: r.status,
    justification: r.justification ?? null,
    requestedByAdminUserId: r.requestedByAdminUserId,
    approvalsCount: r.approvalsCount ?? 0,
    approverAdminUserIds: Array.isArray(r.approverAdminUserIds)
      ? r.approverAdminUserIds.map((v) => Number(v)).filter((v) => Number.isFinite(v))
      : [],
    createdAt: typeof r.createdAt === "string" ? r.createdAt : new Date(r.createdAt).toISOString(),
    updatedAt: typeof r.updatedAt === "string" ? r.updatedAt : new Date(r.updatedAt).toISOString(),
  };
}

export async function createReenableRequest(
  tenantExternalId: string,
  identityExternalId: string,
  body: { justification?: string | null } = {}
): Promise<IdentityReenableRequest> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/identities/${encodeURIComponent(identityExternalId)}/reenable-requests`,
    { method: "POST", headers: headersJson(), body: JSON.stringify(body) }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new Error(err.message ?? err.error ?? `create re-enable request failed: ${res.status}`);
  }
  return parseReenableRequest(await res.json());
}

export async function listReenableRequests(
  tenantExternalId: string,
  identityExternalId: string
): Promise<{ items: IdentityReenableRequest[]; total: number }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/identities/${encodeURIComponent(identityExternalId)}/reenable-requests`,
    { headers: headersJson() }
  );
  if (!res.ok) {
    throw new Error(`list re-enable requests failed: ${res.status}`);
  }
  const json = (await res.json()) as { items?: unknown[]; total?: number };
  return {
    items: Array.isArray(json.items) ? json.items.map(parseReenableRequest) : [],
    total: typeof json.total === "number" ? json.total : 0,
  };
}

export async function approveReenableRequest(
  tenantExternalId: string,
  identityExternalId: string,
  requestExternalId: string
): Promise<IdentityReenableRequest> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/identities/${encodeURIComponent(identityExternalId)}/reenable-requests/${encodeURIComponent(requestExternalId)}/approve`,
    { method: "POST", headers: headersJson() }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new Error(err.message ?? err.error ?? `approve failed: ${res.status}`);
  }
  return parseReenableRequest(await res.json());
}

export async function rejectReenableRequest(
  tenantExternalId: string,
  identityExternalId: string,
  requestExternalId: string,
  body: { reason?: string | null } = {}
): Promise<IdentityReenableRequest> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/identities/${encodeURIComponent(identityExternalId)}/reenable-requests/${encodeURIComponent(requestExternalId)}/reject`,
    { method: "POST", headers: headersJson(), body: JSON.stringify(body) }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new Error(err.message ?? err.error ?? `reject failed: ${res.status}`);
  }
  return parseReenableRequest(await res.json());
}

export async function revokeGovernanceIdentity(
  tenantExternalId: string,
  identityExternalId: string
): Promise<{ revoked: boolean; identityExternalId: string }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/identities/${encodeURIComponent(identityExternalId)}/revoke`,
    { method: "POST", headers: headersJson() }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `revoke failed: ${res.status}`);
  }
  return res.json() as Promise<{ revoked: boolean; identityExternalId: string }>;
}

export async function deactivateGovernanceTenant(
  tenantExternalId: string
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/deactivate`,
    { method: "POST", headers: headersJson() }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `deactivate failed: ${res.status}`);
  }
}

export async function reactivateGovernanceTenant(
  tenantExternalId: string
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/reactivate`,
    { method: "POST", headers: headersJson() }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `reactivate failed: ${res.status}`);
  }
}

export async function fetchGovernanceTenant(
  tenantExternalId: string
): Promise<GovernanceTenantRow> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}`,
    { headers: headersJson() }
  );
  if (!res.ok) {
    throw new Error(`tenant get failed: ${res.status}`);
  }
  return res.json() as Promise<GovernanceTenantRow>;
}

export async function patchGovernanceTenant(
  tenantExternalId: string,
  body: {
    name?: string;
    domain?: string;
    plan?: string;
    dataRegion?: string | null;
    settings?: Record<string, unknown>;
  }
): Promise<GovernanceTenantRow> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}`,
    {
      method: "PATCH",
      headers: headersJson(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    throw new Error(`tenant patch failed: ${res.status}`);
  }
  return res.json() as Promise<GovernanceTenantRow>;
}

export async function createGovernanceIdentity(
  tenantExternalId: string,
  body: {
    type: string;
    identifier: string;
    displayName: string;
    source?: string;
    metadata?: Record<string, unknown>;
  }
): Promise<Record<string, unknown>> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/identities`,
    {
      method: "POST",
      headers: headersJson(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `create identity failed: ${res.status}`);
  }
  return res.json() as Promise<Record<string, unknown>>;
}

export async function fetchGovernanceOnboarding(
  tenantExternalId: string
): Promise<Record<string, unknown>> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/onboarding`,
    { headers: headersJson() }
  );
  if (!res.ok) {
    throw new Error(`onboarding get failed: ${res.status}`);
  }
  const data = (await res.json()) as { onboarding?: Record<string, unknown> };
  return data.onboarding ?? {};
}

/**
 * Error thrown when `PUT /tenants/:id/onboarding` is rejected. Captures both
 * the HTTP status (so callers can distinguish validation 400s from 5xx) and
 * the server's human-readable message (used by the wizard to render a
 * specific, actionable banner and to revert local toggles on UX-09 failures).
 */
export class OnboardingPutError extends Error {
  readonly status: number;
  readonly serverMessage?: string;
  constructor(status: number, message: string, serverMessage?: string) {
    super(message);
    this.name = "OnboardingPutError";
    this.status = status;
    this.serverMessage = serverMessage;
  }
}

export async function putGovernanceOnboarding(
  tenantExternalId: string,
  body: Record<string, unknown>
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/onboarding`,
    {
      method: "PUT",
      headers: headersJson(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    let serverMessage: string | undefined;
    try {
      const payload = (await res.json()) as { error?: string; message?: string };
      serverMessage = payload.message ?? payload.error;
    } catch {
      /* response wasn't JSON; fall through to status-only error */
    }
    throw new OnboardingPutError(
      res.status,
      serverMessage ?? `onboarding put failed: ${res.status}`,
      serverMessage
    );
  }
}

/** Response from `GET /api/v1/admin/platform/summary` (platform admin only). */
export interface PlatformSummary {
  tenants: {
    total: number;
    active: number;
    inactive: number;
  };
  capabilities: {
    postgres: boolean;
    clickhouseDualWrite: boolean;
    siemAuditForward: boolean;
    otel: boolean;
    persistAuditToDb: boolean;
    persistTelemetryToDb: boolean;
    verdiktaMl: boolean;
  };
  generatedAt: string;
}

/**
 * Fleet snapshot for platform operators (MSSP Phase C.1).
 * Requires admin JWT or `ADMIN_API_KEY` as configured in the dashboard.
 */
export async function fetchPlatformSummary(): Promise<PlatformSummary> {
  const res = await fetch(`${API_BASE}/admin/platform/summary`, { headers: headersJson() });
  if (!res.ok) {
    throw new Error(`platform summary failed: ${res.status}`);
  }
  return res.json() as Promise<PlatformSummary>;
}

/** Cross-tenant incident row (platform admin only) — extends tenant-scoped row. */
export interface AdminIncidentRow extends GovernanceIncidentRow {
  tenantId: number;
  tenantExternalId: string | null;
  tenantName: string | null;
}

/**
 * Cross-tenant high-severity incident aggregate for the operator console.
 * Backed by `IncidentService.listAcrossTenants` via `/admin/operations/incidents`.
 */
export async function fetchAdminIncidents(
  options?: { severities?: string[]; limit?: number }
): Promise<{ items: AdminIncidentRow[] }> {
  const params = new URLSearchParams();
  if (options?.severities?.length) params.set("severities", options.severities.join(","));
  if (options?.limit) params.set("limit", String(options.limit));
  const q = params.toString() ? `?${params.toString()}` : "";
  const res = await governanceApiFetch(`/admin/operations/incidents${q}`);
  if (!res.ok) {
    throw new Error(`admin incidents failed: ${res.status}`);
  }
  return res.json() as Promise<{ items: AdminIncidentRow[] }>;
}

/** OPS-05 — operator incident row (API returns ISO date strings). */
export interface GovernanceIncidentRow {
  id: number;
  externalId: string;
  title: string;
  status: string;
  severity: string;
  body: string | null;
  mitreTactic?: string | null;
  mitreTechnique?: string | null;
  assignee?: string | null;
  timeline: Array<{ at: string; kind: string; message: string }>;
  createdAt: string;
  updatedAt: string;
}

export async function fetchGovernanceIncidents(
  tenantExternalId: string
): Promise<{ items: GovernanceIncidentRow[] }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/incidents`,
    { headers: headersJson() }
  );
  if (!res.ok) {
    throw new Error(`incidents list failed: ${res.status}`);
  }
  return res.json() as Promise<{ items: GovernanceIncidentRow[] }>;
}

export async function fetchGovernanceIncident(
  tenantExternalId: string,
  externalId: string
): Promise<GovernanceIncidentRow> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/incidents/${encodeURIComponent(externalId)}`,
    { headers: headersJson() }
  );
  if (!res.ok) {
    throw new Error(`incident get failed: ${res.status}`);
  }
  return res.json() as Promise<GovernanceIncidentRow>;
}

export async function createGovernanceIncident(
  tenantExternalId: string,
  body: { title: string; severity?: string; body?: string | null }
): Promise<GovernanceIncidentRow> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/incidents`,
    {
      method: "POST",
      headers: headersJson(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `incident create failed: ${res.status}`);
  }
  return res.json() as Promise<GovernanceIncidentRow>;
}

export interface GovernanceDataSubjectRequestRow {
  id: string;
  type: "export" | "erasure" | "restriction";
  status: "open" | "in_progress" | "completed" | "cancelled";
  subjectIdentifier: string;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}

export async function fetchGovernanceDataSubjectRequests(
  tenantExternalId: string
): Promise<{ items: GovernanceDataSubjectRequestRow[] }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/gdpr/requests`,
    { headers: headersJson() }
  );
  if (!res.ok) {
    throw new Error(`DSR list failed: ${res.status}`);
  }
  return res.json() as Promise<{ items: GovernanceDataSubjectRequestRow[] }>;
}

export async function createGovernanceDataSubjectRequest(
  tenantExternalId: string,
  body: {
    type: GovernanceDataSubjectRequestRow["type"];
    subjectIdentifier: string;
    notes?: string | null;
  }
): Promise<GovernanceDataSubjectRequestRow> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/gdpr/requests`,
    {
      method: "POST",
      headers: headersJson(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `DSR create failed: ${res.status}`);
  }
  return res.json() as Promise<GovernanceDataSubjectRequestRow>;
}

export async function patchGovernanceDataSubjectRequest(
  tenantExternalId: string,
  requestExternalId: string,
  body: {
    status?: GovernanceDataSubjectRequestRow["status"];
    notes?: string | null;
  }
): Promise<GovernanceDataSubjectRequestRow> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/gdpr/requests/${encodeURIComponent(requestExternalId)}`,
    {
      method: "PATCH",
      headers: headersJson(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `DSR patch failed: ${res.status}`);
  }
  return res.json() as Promise<GovernanceDataSubjectRequestRow>;
}

export async function patchGovernanceIncident(
  tenantExternalId: string,
  externalId: string,
  body: { status?: string; note?: string }
): Promise<GovernanceIncidentRow> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/incidents/${encodeURIComponent(externalId)}`,
    {
      method: "PATCH",
      headers: headersJson(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `incident patch failed: ${res.status}`);
  }
  return res.json() as Promise<GovernanceIncidentRow>;
}

export type GovernanceCustomTenantRole = {
  id: number;
  tenantId: number;
  externalId: string;
  name: string;
  permissionKeys: string[];
  createdAt: string;
  updatedAt: string;
};

export async function fetchCustomRolePermissionCatalogue(
  tenantExternalId: string
): Promise<{ keys: string[] }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/custom-roles/permissions-catalogue`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`custom role catalogue failed: ${res.status}`);
  return res.json() as Promise<{ keys: string[] }>;
}

export async function fetchCustomTenantRoles(
  tenantExternalId: string
): Promise<{ items: GovernanceCustomTenantRole[] }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/custom-roles`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`custom roles list failed: ${res.status}`);
  return res.json() as Promise<{ items: GovernanceCustomTenantRole[] }>;
}

export async function createCustomTenantRole(
  tenantExternalId: string,
  body: { externalId: string; name: string; permissionKeys: string[] }
): Promise<GovernanceCustomTenantRole> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/custom-roles`,
    {
      method: "POST",
      headers: headersJson(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `create custom role failed: ${res.status}`);
  }
  return res.json() as Promise<GovernanceCustomTenantRole>;
}

export async function patchCustomTenantRole(
  tenantExternalId: string,
  roleExternalId: string,
  body: { name?: string; permissionKeys?: string[] }
): Promise<GovernanceCustomTenantRole> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/custom-roles/${encodeURIComponent(roleExternalId)}`,
    {
      method: "PATCH",
      headers: headersJson(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `patch custom role failed: ${res.status}`);
  }
  return res.json() as Promise<GovernanceCustomTenantRole>;
}

export async function deleteCustomTenantRole(
  tenantExternalId: string,
  roleExternalId: string
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/custom-roles/${encodeURIComponent(roleExternalId)}`,
    { method: "DELETE", headers: headersJson() }
  );
  if (!res.ok && res.status !== 204) {
    throw new Error(`delete custom role failed: ${res.status}`);
  }
}

export type GovernanceTenantIpRule = {
  id: number;
  tenantId: number;
  externalId: string;
  cidr: string;
  action: "allow" | "deny";
  priority: number;
  enabled: boolean;
  note: string | null;
  createdAt: string;
  updatedAt: string;
};

export async function fetchGovernanceTenantIpRules(
  tenantExternalId: string
): Promise<{ items: GovernanceTenantIpRule[] }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/ip-rules`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`ip rules list failed: ${res.status}`);
  return res.json() as Promise<{ items: GovernanceTenantIpRule[] }>;
}

export async function createGovernanceTenantIpRule(
  tenantExternalId: string,
  body: {
    externalId: string;
    cidr: string;
    action: "allow" | "deny";
    priority?: number;
    enabled?: boolean;
    note?: string | null;
  }
): Promise<GovernanceTenantIpRule> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/ip-rules`,
    {
      method: "POST",
      headers: headersJson(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `create ip rule failed: ${res.status}`);
  }
  return res.json() as Promise<GovernanceTenantIpRule>;
}

export async function patchGovernanceTenantIpRule(
  tenantExternalId: string,
  ruleExternalId: string,
  body: {
    cidr?: string;
    action?: "allow" | "deny";
    priority?: number;
    enabled?: boolean;
    note?: string | null;
  }
): Promise<GovernanceTenantIpRule> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/ip-rules/${encodeURIComponent(ruleExternalId)}`,
    {
      method: "PATCH",
      headers: headersJson(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `patch ip rule failed: ${res.status}`);
  }
  return res.json() as Promise<GovernanceTenantIpRule>;
}

export async function deleteGovernanceTenantIpRule(
  tenantExternalId: string,
  ruleExternalId: string
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/ip-rules/${encodeURIComponent(ruleExternalId)}`,
    { method: "DELETE", headers: headersJson() }
  );
  if (!res.ok && res.status !== 204) {
    throw new Error(`delete ip rule failed: ${res.status}`);
  }
}

// ─── ITDR Phase 1 — Armour Graph + session recording analysis ───────────────

export type ArmourGraphNodeType =
  | "tenant_context"
  | "identity"
  | "policy"
  | "elevation"
  | "secret_lease"
  | "session"
  | "escalation_path"
  // RFC-004 R4.4 — agentic node classes (additive, same node shape).
  | "agent"
  | "nhi"
  | "delegation_chain";

export type ArmourGraphNode = {
  id: string;
  type: ArmourGraphNodeType;
  data: {
    label: string;
    subtitle?: string;
    meta?: Record<string, unknown>;
  };
};

export type ArmourGraphEdge = {
  id: string;
  source: string;
  target: string;
  label?: string;
  meta?: Record<string, unknown>;
};

export type ArmourGraphClassCounts = {
  identity: number;
  policy: number;
  elevation: number;
  secret_lease: number;
  session: number;
  /** RFC-004 R4.4 — optional agentic classes (older cached graphs omit them). */
  agent?: number;
  nhi?: number;
  delegation_chain?: number;
};

export type ArmourGraphMeta = {
  totals: ArmourGraphClassCounts;
  shown: ArmourGraphClassCounts;
  truncated: boolean;
};

export type ArmourGraphPayload = {
  nodes: ArmourGraphNode[];
  edges: ArmourGraphEdge[];
  meta?: ArmourGraphMeta;
};

export async function fetchTenantArmourGraph(
  tenantExternalId: string,
  options: { includeEscalation?: boolean; forceLive?: boolean } = {}
): Promise<ArmourGraphPayload> {
  const params = new URLSearchParams();
  if (options.includeEscalation) params.set("include", "escalation");
  if (options.forceLive) params.set("forceLive", "1");
  const query = params.toString();
  const url =
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/graph` +
    (query ? `?${query}` : "");
  const res = await fetch(url, { headers: headersJson() });
  if (!res.ok) {
    throw new Error(`armour graph failed: ${res.status}`);
  }
  return res.json() as Promise<ArmourGraphPayload>;
}

/**
 * P2 (live focus-load) — fetch one identity's full, unbounded 1-hop
 * neighbourhood so an operator can expand it beyond the global graph caps.
 */
export async function fetchArmourGraphFocus(
  tenantExternalId: string,
  identityExternalId: string
): Promise<ArmourGraphPayload> {
  const url =
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/graph/focus` +
    `?identity=${encodeURIComponent(identityExternalId)}`;
  const res = await fetch(url, { headers: headersJson() });
  if (!res.ok) {
    throw new Error(`armour graph focus failed: ${res.status}`);
  }
  return res.json() as Promise<ArmourGraphPayload>;
}

/** Phase 2 (OOS) — operator-triggered cache refresh for one tenant. */
export async function refreshTenantArmourGraph(
  tenantExternalId: string
): Promise<{ ok: boolean }> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/graph/refresh`,
    { method: "POST" }
  );
  if (!res.ok) {
    throw new Error(`armour graph refresh failed: ${res.status}`);
  }
  return res.json() as Promise<{ ok: boolean }>;
}

export type ArmourGraphTrimScope = "node" | "branch";
export type ArmourGraphTrimDisposition = "suspend" | "quarantine" | "revoke";

export type ArmourGraphTrimDisposedItem = {
  type: "session" | "secret_lease" | "elevation" | "identity";
  externalId: string;
  status: "disposed" | "already_disposed";
};

export type ArmourGraphTrimFailedItem = {
  type: "session" | "secret_lease" | "elevation" | "identity";
  externalId: string;
  error: string;
};

export type ArmourGraphTrimResult = {
  auditGroupId: string;
  disposed: ArmourGraphTrimDisposedItem[];
  failed: ArmourGraphTrimFailedItem[];
};

/** Dispose a graph node (and optionally its branch) via the permissioned trim endpoint. */
export async function trimArmourGraphNode(
  tenantExternalId: string,
  body: {
    nodeId: string;
    scope: ArmourGraphTrimScope;
    disposition?: ArmourGraphTrimDisposition;
    reason?: string;
  }
): Promise<ArmourGraphTrimResult> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/graph/trim`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? `armour graph trim failed: ${res.status}`);
  }
  return res.json() as Promise<ArmourGraphTrimResult>;
}

export type SessionRecordingAnalysisDto = {
  recordingId: number;
  status: string;
  anomalyScore: number | null;
  heatmapData: unknown;
  summary: string | null;
  errorMessage: string | null;
  updatedAt: string | null;
};

export async function fetchSessionRecordingAnalysis(
  tenantExternalId: string,
  sessionId: string
): Promise<SessionRecordingAnalysisDto> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/recordings/${encodeURIComponent(sessionId)}/analysis`,
    { headers: headersJson() }
  );
  if (!res.ok) {
    throw new Error(`session recording analysis failed: ${res.status}`);
  }
  return res.json() as Promise<SessionRecordingAnalysisDto>;
}

export type SessionRecordingPlaybackDto = {
  manifestUrl: string;
  expiresIn: number;
  blob: {
    manifestUri: string | null;
    codec: string | null;
    durationSeconds: number | null;
    sizeBytes: number | null;
    segmentCount: number;
    redactionAppliedAt: string | null;
    uploadedAt: string | null;
  };
};

/** Phase 4 (OOS) — short-lived presigned playback URL for a recording's manifest. */
export async function fetchSessionRecordingPlayback(
  tenantExternalId: string,
  sessionId: string
): Promise<SessionRecordingPlaybackDto | null> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/recordings/${encodeURIComponent(sessionId)}/playback`,
    { headers: headersJson() }
  );
  if (res.status === 404 || res.status === 503) {
    return null;
  }
  if (!res.ok) {
    throw new Error(`session recording playback failed: ${res.status}`);
  }
  return res.json() as Promise<SessionRecordingPlaybackDto>;
}

export type SegmentUploadTicketDto = {
  uploadUrl: string;
  key: string;
  expiresIn: number;
};

/** Phase 4 (OOS) — presigned PUT URL for a single segment upload. */
export async function requestSegmentUploadTicket(
  tenantExternalId: string,
  sessionId: string,
  body: { sequence: number; extension?: "webm" | "mp4" | "ts" | "m3u8" }
): Promise<SegmentUploadTicketDto> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/recordings/${encodeURIComponent(sessionId)}/segments`,
    {
      method: "POST",
      headers: headersJson(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    throw new Error(`segment upload presign failed: ${res.status}`);
  }
  return res.json() as Promise<SegmentUploadTicketDto>;
}

export type ManifestUploadResultDto = SegmentUploadTicketDto & {
  blob: SessionRecordingPlaybackDto["blob"];
};

// ── Phase 5 ITDR (OOS) — decoy credential management ────────────────────────

export type DecoySecretDto = {
  id: number;
  tenantId: number;
  kind: "api_key" | "password" | "aws_access_key" | "token" | "session_cookie";
  displayHint: string | null;
  severity: "low" | "medium" | "high" | "critical";
  plantedAt: string;
  plantedByAdminId: number | null;
  isActive: boolean;
  lastSeenAttemptAt: string | null;
  attemptCount: number;
};

export type MintDecoyResultDto = {
  plaintext: string;
  decoy: DecoySecretDto;
};

export async function listDecoySecrets(
  tenantExternalId: string
): Promise<{ decoys: DecoySecretDto[] }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/decoy-secrets`,
    { headers: headersJson() }
  );
  if (!res.ok) {
    throw new Error(`list decoys failed: ${res.status}`);
  }
  return res.json() as Promise<{ decoys: DecoySecretDto[] }>;
}

export async function mintDecoySecret(
  tenantExternalId: string,
  body: { kind: DecoySecretDto["kind"]; displayHint?: string; severity?: DecoySecretDto["severity"] }
): Promise<MintDecoyResultDto> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/decoy-secrets`,
    {
      method: "POST",
      headers: headersJson(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    throw new Error(`mint decoy failed: ${res.status}`);
  }
  return res.json() as Promise<MintDecoyResultDto>;
}

export async function deactivateDecoySecret(
  tenantExternalId: string,
  id: number
): Promise<{ ok: boolean }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/decoy-secrets/${id}`,
    { method: "DELETE", headers: headersJson() }
  );
  if (!res.ok) {
    throw new Error(`deactivate decoy failed: ${res.status}`);
  }
  return res.json() as Promise<{ ok: boolean }>;
}

// ── Phase 2 — Agentic AI + vault + CIEM ─────────────────────────────────────

export type AiAgentKindDto =
  | "llm_orchestrator"
  | "data_pipeline"
  | "autonomous_bot"
  | "other";

export type AiAgentMetadataDto = {
  id: number;
  tenantId: number;
  identityId: number;
  agentKind: AiAgentKindDto;
  modelReference: string | null;
  approvedIntents: string[];
  ownerIdentityId: number;
  createdAt: string;
  updatedAt: string;
};

export async function listAiAgents(
  tenantExternalId: string
): Promise<{ agents: AiAgentMetadataDto[] }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/ai-agents`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`list ai agents failed: ${res.status}`);
  return res.json() as Promise<{ agents: AiAgentMetadataDto[] }>;
}

export async function createAiAgentMetadata(
  tenantExternalId: string,
  body: {
    identityExternalId: string;
    agentKind: AiAgentKindDto;
    modelReference?: string | null;
    approvedIntents: string[];
    ownerIdentityExternalId: string;
  }
): Promise<{ agent: AiAgentMetadataDto }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/ai-agents`,
    {
      method: "POST",
      headers: headersJson(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(body.error ?? `create ai agent failed: ${res.status}`);
  }
  return res.json() as Promise<{ agent: AiAgentMetadataDto }>;
}

export async function patchAiAgentMetadata(
  tenantExternalId: string,
  id: number,
  body: Partial<{
    agentKind: AiAgentKindDto;
    modelReference: string | null;
    approvedIntents: string[];
    ownerIdentityExternalId: string;
  }>
): Promise<{ agent: AiAgentMetadataDto }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/ai-agents/${id}`,
    {
      method: "PATCH",
      headers: headersJson(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) throw new Error(`patch ai agent failed: ${res.status}`);
  return res.json() as Promise<{ agent: AiAgentMetadataDto }>;
}

export async function deleteAiAgentMetadata(
  tenantExternalId: string,
  id: number
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/ai-agents/${id}`,
    { method: "DELETE", headers: headersJson() }
  );
  if (!res.ok) throw new Error(`delete ai agent failed: ${res.status}`);
}

export type AiAgentSuggestionStatus = "pending" | "accepted" | "dismissed";

export type AiAgentSuggestionDto = {
  id: number;
  tenantId: number;
  source: string;
  fingerprint: string;
  suggestedKind: AiAgentKindDto | null;
  suggestedIntents: string[];
  payload: Record<string, unknown>;
  status: AiAgentSuggestionStatus;
  linkedIdentityId: number | null;
  linkedIdentityExternalId: string | null;
  createdAt: string;
  updatedAt: string;
};

export async function listAiAgentSuggestions(
  tenantExternalId: string,
  status?: AiAgentSuggestionStatus
): Promise<{ suggestions: AiAgentSuggestionDto[] }> {
  const qs = new URLSearchParams();
  if (status) qs.set("status", status);
  const q = qs.toString();
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/ai-agents/suggestions${q ? `?${q}` : ""}`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`list ai agent suggestions failed: ${res.status}`);
  return res.json() as Promise<{ suggestions: AiAgentSuggestionDto[] }>;
}

export async function dismissAiAgentSuggestion(
  tenantExternalId: string,
  id: number
): Promise<{ suggestion: AiAgentSuggestionDto }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/ai-agents/suggestions/${id}/dismiss`,
    { method: "POST", headers: headersJson() }
  );
  if (!res.ok) throw new Error(`dismiss suggestion failed: ${res.status}`);
  return res.json() as Promise<{ suggestion: AiAgentSuggestionDto }>;
}

export async function promoteAiAgentSuggestion(
  tenantExternalId: string,
  id: number,
  body: {
    identityExternalId: string;
    ownerIdentityExternalId: string;
    agentKind?: AiAgentKindDto;
    modelReference?: string | null;
    approvedIntents?: string[];
  }
): Promise<{ agent: AiAgentMetadataDto }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/ai-agents/suggestions/${id}/promote`,
    {
      method: "POST",
      headers: headersJson(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) throw new Error(`promote suggestion failed: ${res.status}`);
  return res.json() as Promise<{ agent: AiAgentMetadataDto }>;
}

export type VaultSecretSummaryDto = {
  id: number;
  externalId: string;
  name: string;
  description: string | null;
  kmsKeyId: string;
  version: number;
  rotationPolicy: Record<string, unknown> | null;
  lastRotatedAt: string | null;
  nextRotationAt: string | null;
  rotationStatus: string;
  createdAt: string;
  updatedAt: string;
};

export async function listVaultSecrets(
  tenantExternalId: string
): Promise<{ secrets: VaultSecretSummaryDto[] }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/vault/secrets`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`list vault secrets failed: ${res.status}`);
  return res.json() as Promise<{ secrets: VaultSecretSummaryDto[] }>;
}

export type SecretLeaseStatusFilter = "active" | "revoked" | "expired" | "all";

export type SecretLeaseSummaryDto = {
  externalId: string;
  identityExternalId?: string;
  identityDisplayName?: string | null;
  identityIdentifier?: string | null;
  identityId: number;
  resourceType: string;
  resourceRef: string;
  provider: string;
  leaseTtlSeconds: number;
  secretRef: string;
  expiresAt: string;
  status: string;
  revokedAt: string | null;
  renewedAt: string | null;
  createdAt: string;
  providerMetadata?: Record<string, unknown>;
  elevationGrantId?: number | null;
  elevationGrantExternalId?: string | null;
  issuanceSource?: string | null;
  matchedScope?: string | null;
};

export async function listSecretLeases(
  tenantExternalId: string,
  opts: {
    identityExternalId?: string;
    status?: SecretLeaseStatusFilter;
    limit?: number;
  } = {}
): Promise<{ leases: SecretLeaseSummaryDto[] }> {
  const q = new URLSearchParams();
  if (opts.identityExternalId) q.set("identityExternalId", opts.identityExternalId);
  if (opts.status) q.set("status", opts.status);
  if (opts.limit != null) q.set("limit", String(opts.limit));
  const suffix = q.toString() ? `?${q.toString()}` : "";
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/leases${suffix}`
  );
  if (!res.ok) throw new Error(`list secret leases failed: ${res.status}`);
  return res.json() as Promise<{ leases: SecretLeaseSummaryDto[] }>;
}

export async function fetchSecretLease(
  tenantExternalId: string,
  leaseExternalId: string
): Promise<{ lease: SecretLeaseSummaryDto }> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/leases/${encodeURIComponent(leaseExternalId)}`
  );
  if (!res.ok) throw new Error(`get secret lease failed: ${res.status}`);
  return res.json() as Promise<{ lease: SecretLeaseSummaryDto }>;
}

export async function listSecretLeasesForGrant(
  tenantExternalId: string,
  grantExternalId: string
): Promise<{ leases: SecretLeaseSummaryDto[] }> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/elevations/${encodeURIComponent(grantExternalId)}/leases`
  );
  if (!res.ok) throw new Error(`list grant leases failed: ${res.status}`);
  return res.json() as Promise<{ leases: SecretLeaseSummaryDto[] }>;
}

export async function renewSecretLease(
  tenantExternalId: string,
  leaseExternalId: string,
  extendSeconds?: number
): Promise<{ lease: SecretLeaseSummaryDto }> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/leases/${encodeURIComponent(leaseExternalId)}/renew`,
    {
      method: "POST",
      body: JSON.stringify(extendSeconds != null ? { extendSeconds } : {}),
    }
  );
  if (!res.ok) throw new Error(`renew secret lease failed: ${res.status}`);
  return res.json() as Promise<{ lease: SecretLeaseSummaryDto }>;
}

export async function revokeSecretLease(
  tenantExternalId: string,
  leaseExternalId: string
): Promise<{ lease: SecretLeaseSummaryDto }> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/leases/${encodeURIComponent(leaseExternalId)}/revoke`,
    { method: "POST", body: JSON.stringify({}) }
  );
  if (!res.ok) throw new Error(`revoke secret lease failed: ${res.status}`);
  return res.json() as Promise<{ lease: SecretLeaseSummaryDto }>;
}

export async function createVaultSecret(
  tenantExternalId: string,
  body: {
    name: string;
    description?: string | null;
    secret: string;
    rotationFrequencyDays?: number | null;
  }
): Promise<{ secret: { externalId: string; name: string; version: number; nextRotationAt: string | null } }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/vault/secrets`,
    {
      method: "POST",
      headers: headersJson(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) throw new Error(`create vault secret failed: ${res.status}`);
  return res.json() as Promise<{
    secret: { externalId: string; name: string; version: number; nextRotationAt: string | null };
  }>;
}

export async function revealVaultSecret(
  tenantExternalId: string,
  externalId: string
): Promise<{ plaintext: string }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/vault/secrets/${encodeURIComponent(externalId)}/reveal`,
    {
      method: "POST",
      headers: headersJson(),
    }
  );
  if (!res.ok) throw new Error(`reveal vault secret failed: ${res.status}`);
  return res.json() as Promise<{ plaintext: string }>;
}

export async function deleteVaultSecret(
  tenantExternalId: string,
  externalId: string
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/vault/secrets/${encodeURIComponent(externalId)}`,
    { method: "DELETE", headers: headersJson() }
  );
  if (!res.ok) throw new Error(`delete vault secret failed: ${res.status}`);
}

export type CiemRecommendationDto = {
  id: number;
  tenantId: number;
  cloud: string;
  principalArn: string;
  recommendationType: string;
  severity: string | null;
  summary: string | null;
  evidence: Record<string, unknown>;
  identityExternalId: string | null;
  createdAt: string;
};

export async function listCiemRecommendations(
  tenantExternalId: string,
  limit?: number
): Promise<{ recommendations: CiemRecommendationDto[] }> {
  const qs = new URLSearchParams();
  if (limit != null) qs.set("limit", String(limit));
  const q = qs.toString();
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/ciem/recommendations${q ? `?${q}` : ""}`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`list ciem failed: ${res.status}`);
  return res.json() as Promise<{ recommendations: CiemRecommendationDto[] }>;
}

export async function requestManifestUploadTicket(
  tenantExternalId: string,
  sessionId: string,
  body: {
    manifestUri?: string;
    codec?: string;
    durationSeconds?: number;
    sizeBytes?: number;
    segmentCount?: number;
  } = {}
): Promise<ManifestUploadResultDto> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/recordings/${encodeURIComponent(sessionId)}/manifest`,
    {
      method: "POST",
      headers: headersJson(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    throw new Error(`manifest upload presign failed: ${res.status}`);
  }
  return res.json() as Promise<ManifestUploadResultDto>;
}

// ─── Phase 4 Zero-Trust: Remote Access ───────────────────────────────────────

export interface RemoteTargetDto {
  id: number;
  tenantId: number;
  name: string;
  protocol: "ssh" | "rdp" | "vnc" | "http";
  hostname: string;
  port: number;
  vaultSecretId: number | null;
  tags: string[];
  createdByAdminId: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface RemoteSessionDto {
  id: number;
  tenantId: number;
  targetId: number;
  identityExternalId: string;
  tokenConsumed: boolean;
  connectedAt: string | null;
  disconnectedAt: string | null;
  recordingSessionId: string | null;
  createdAt: string;
}

export interface ConnectTargetResultDto {
  token: string;
  expiresAt: string;
  connection: { protocol: string; hostname: string; port: number };
  proxyUrl: string | null;
  sessionId: number;
}

export async function listRemoteTargets(
  tenantExternalId: string,
  opts: { limit?: number; offset?: number } = {}
): Promise<{ items: RemoteTargetDto[]; total: number }> {
  const q = new URLSearchParams();
  if (opts.limit !== undefined) q.set("limit", String(opts.limit));
  if (opts.offset !== undefined) q.set("offset", String(opts.offset));
  const res = await fetch(
    `${API_BASE}/tenants/${tenantExternalId}/remote-access/targets?${q}`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`listRemoteTargets failed: ${res.status}`);
  return res.json() as Promise<{ items: RemoteTargetDto[]; total: number }>;
}

export async function createRemoteTarget(
  tenantExternalId: string,
  body: {
    name: string;
    protocol: RemoteTargetDto["protocol"];
    hostname: string;
    port: number;
    vaultSecretId?: number | null;
    tags?: string[];
  }
): Promise<RemoteTargetDto> {
  const res = await fetch(
    `${API_BASE}/tenants/${tenantExternalId}/remote-access/targets`,
    { method: "POST", headers: headersJson(), body: JSON.stringify(body) }
  );
  if (!res.ok) throw new Error(`createRemoteTarget failed: ${res.status}`);
  return res.json() as Promise<RemoteTargetDto>;
}

export async function updateRemoteTarget(
  tenantExternalId: string,
  id: number,
  patch: Partial<Pick<RemoteTargetDto, "name" | "protocol" | "hostname" | "port" | "vaultSecretId" | "tags">>
): Promise<RemoteTargetDto> {
  const res = await fetch(
    `${API_BASE}/tenants/${tenantExternalId}/remote-access/targets/${id}`,
    { method: "PATCH", headers: headersJson(), body: JSON.stringify(patch) }
  );
  if (!res.ok) throw new Error(`updateRemoteTarget failed: ${res.status}`);
  return res.json() as Promise<RemoteTargetDto>;
}

export async function deleteRemoteTarget(
  tenantExternalId: string,
  id: number
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/tenants/${tenantExternalId}/remote-access/targets/${id}`,
    { method: "DELETE", headers: headersJson() }
  );
  if (!res.ok) throw new Error(`deleteRemoteTarget failed: ${res.status}`);
}

export async function connectRemoteTarget(
  tenantExternalId: string,
  id: number
): Promise<ConnectTargetResultDto> {
  const res = await fetch(
    `${API_BASE}/tenants/${tenantExternalId}/remote-access/targets/${id}/connect`,
    { method: "POST", headers: headersJson() }
  );
  if (!res.ok) throw new Error(`connectRemoteTarget failed: ${res.status}`);
  return res.json() as Promise<ConnectTargetResultDto>;
}

export async function listRemoteSessions(
  tenantExternalId: string,
  opts: { targetId?: number; activeOnly?: boolean; limit?: number; offset?: number } = {}
): Promise<{ items: RemoteSessionDto[]; total: number }> {
  const q = new URLSearchParams();
  if (opts.targetId !== undefined) q.set("targetId", String(opts.targetId));
  if (opts.activeOnly) q.set("activeOnly", "1");
  if (opts.limit !== undefined) q.set("limit", String(opts.limit));
  if (opts.offset !== undefined) q.set("offset", String(opts.offset));
  const res = await fetch(
    `${API_BASE}/tenants/${tenantExternalId}/remote-access/sessions?${q}`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`listRemoteSessions failed: ${res.status}`);
  return res.json() as Promise<{ items: RemoteSessionDto[]; total: number }>;
}

// ─── Phase 4 Zero-Trust: Endpoint Agents ─────────────────────────────────────

export interface EndpointAgentDto {
  id: number;
  tenantId: number;
  externalId: string;
  hostname: string;
  os: "windows" | "macos" | "linux";
  osVersion: string | null;
  agentVersion: string | null;
  status: "online" | "offline" | "degraded";
  lastHeartbeat: string | null;
  identityExternalId: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export async function listEndpointAgents(
  tenantExternalId: string,
  opts: { status?: EndpointAgentDto["status"]; limit?: number; offset?: number } = {}
): Promise<{ items: EndpointAgentDto[]; total: number }> {
  const q = new URLSearchParams();
  if (opts.status) q.set("status", opts.status);
  if (opts.limit !== undefined) q.set("limit", String(opts.limit));
  if (opts.offset !== undefined) q.set("offset", String(opts.offset));
  const res = await fetch(
    `${API_BASE}/tenants/${tenantExternalId}/endpoint-agents?${q}`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`listEndpointAgents failed: ${res.status}`);
  return res.json() as Promise<{ items: EndpointAgentDto[]; total: number }>;
}

export async function getEndpointAgent(
  tenantExternalId: string,
  externalId: string
): Promise<EndpointAgentDto> {
  const res = await fetch(
    `${API_BASE}/tenants/${tenantExternalId}/endpoint-agents/${encodeURIComponent(externalId)}`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`getEndpointAgent failed: ${res.status}`);
  return res.json() as Promise<EndpointAgentDto>;
}

export async function deregisterEndpointAgent(
  tenantExternalId: string,
  externalId: string
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/tenants/${tenantExternalId}/endpoint-agents/${encodeURIComponent(externalId)}`,
    { method: "DELETE", headers: headersJson() }
  );
  if (!res.ok) throw new Error(`deregisterEndpointAgent failed: ${res.status}`);
}

// ─── Decision traces / flight recorder (Innovation Bet #5) ────────────────────

export interface GovernanceDecisionTrace {
  id: number;
  sessionId: string;
  identityExternalId: string | null;
  evaluatedAt: string;
  recordedScore: number;
  recordedAction: string;
  digest: string;
  alg: string;
  keyId: string;
  createdAt: string;
  /**
   * Epic A (A5): the full signed trace, when returned by the API. Carries the
   * calibrated score/band and the complete signed rationale (reasoning +
   * contributions + evidence quality) for auditor review.
   */
  signedTrace?: {
    trace?: {
      rawScore?: number;
      calibratedScore?: number;
      calibratedBand?: string;
      reasoning?: string;
      evidenceQuality?: number;
      contributions?: Array<{ vector: string; score: number; marginalContribution: number }>;
    };
  };
}

export interface DecisionTraceVerifyResult {
  id: number;
  signatureValid: boolean;
  faithful: boolean;
  reason?: string;
  alg: string;
  keyId: string;
  keyGeneration?: number;
}

export async function fetchDecisionTraces(
  tenantExternalId: string,
  params?: { limit?: number; offset?: number; sessionId?: string }
): Promise<{ items: GovernanceDecisionTrace[]; total: number }> {
  const qs = new URLSearchParams();
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  if (params?.sessionId) qs.set("sessionId", params.sessionId);
  const q = qs.toString();
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/decision-traces${q ? `?${q}` : ""}`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`decision-traces failed: ${res.status}`);
  return res.json();
}

/** Bulk SIEM export of signed decision traces as NDJSON or CEF text (Bet #5). */
export async function exportDecisionTracesSiem(
  tenantExternalId: string,
  opts?: { format?: "ndjson" | "cef"; since?: string; limit?: number }
): Promise<string> {
  const qs = new URLSearchParams();
  if (opts?.format) qs.set("format", opts.format);
  if (opts?.since) qs.set("since", opts.since);
  if (opts?.limit != null) qs.set("limit", String(opts.limit));
  const q = qs.toString();
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/decision-traces/siem-export${q ? `?${q}` : ""}`
  );
  if (!res.ok) throw new Error(`decision-trace export failed: ${res.status}`);
  return res.text();
}

export async function verifyDecisionTrace(
  tenantExternalId: string,
  id: number
): Promise<DecisionTraceVerifyResult> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/decision-traces/${id}/verify`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`verify trace failed: ${res.status}`);
  return res.json();
}

// ─── Trust-SLA attainment (Innovation Bet #9) ─────────────────────────────────

export interface TrustSlaObjectiveResult {
  objectiveId: string;
  label: string;
  budgetMs: number;
  targetAttainment: number;
  sampleCount: number;
  attainment: number;
  p50Ms: number;
  p95Ms: number;
  p99Ms: number;
  met: boolean;
}

export interface TrustSlaBreach {
  objectiveId: string;
  severity: "warning" | "critical";
  attainment: number;
  targetAttainment: number;
  sampleCount: number;
  p99Ms: number;
  status: string;
  detectedAt: string;
}

export interface SignedSlaAttestation {
  claims: {
    tenantId: string;
    windowMs: number;
    issuedAt: string;
    propagationBudgetMs?: number;
    objectives: Array<{
      objectiveId: string;
      attainment: number;
      targetAttainment: number;
      sampleCount: number;
      p99Ms: number;
      met: boolean;
    }>;
  };
  digest: string;
  signature: string;
  alg: string;
  keyId: string;
}

export async function fetchTrustSla(
  tenantExternalId: string,
  windowMs?: number
): Promise<{
  windowMs: number;
  sampleCount: number;
  objectives: TrustSlaObjectiveResult[];
  breaches: TrustSlaBreach[];
  attestation?: SignedSlaAttestation;
  cascade?: { budgetMs: number; failClosed: boolean };
}> {
  const qs = windowMs != null ? `?windowMs=${windowMs}` : "";
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/trust-sla${qs}`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`trust-sla failed: ${res.status}`);
  return res.json();
}

// ─── Decision-as-code policy simulation (Innovation Bet #6) ───────────────────

export interface PolicySimulationRequest {
  thresholds: { allow: number; downgrade: number; challenge: number; revoke: number };
  weights?: Record<string, number>;
  windowMs?: number;
  maxSamples?: number;
  /** Historical record set to replay against. */
  source?: "telemetry" | "decision_traces";
}

export interface PolicySimulationResponse {
  total: number;
  changed: number;
  unchanged: number;
  changedFraction: number;
  transitions: Record<string, number>;
  newlyRevoked: number;
  newlyAllowed: number;
  sampledFrom: number;
  source?: "telemetry" | "decision_traces";
  sample: Array<{ sessionId: string; from: string; to: string; fromScore: number; toScore: number }>;
  simulationRunId?: string;
}

export interface PolicySimulationApplyResult {
  status: "applied" | "rolled_back";
  applicationId: number;
  simulationRunId: string;
  policyExternalId: string | null;
  policyVersion: number | null;
  spikeFraction: number;
  postApplySpikeFraction: number;
  rolledBack: boolean;
}

export async function runPolicySimulation(
  tenantExternalId: string,
  body: PolicySimulationRequest
): Promise<PolicySimulationResponse> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/policy-simulations`,
    { method: "POST", headers: headersJson(), body: JSON.stringify(body) }
  );
  if (!res.ok) throw new Error(`policy-simulation failed: ${res.status}`);
  return res.json();
}

export async function applyPolicySimulation(
  tenantExternalId: string,
  body: {
    simulationRunId: string;
    policyExternalId?: string;
    maxRevokeSpikeFraction?: number;
    force?: boolean;
  }
): Promise<PolicySimulationApplyResult> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/policy-simulations/apply`,
    { method: "POST", headers: headersJson(), body: JSON.stringify(body) }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? `policy-simulation apply failed: ${res.status}`);
  }
  return res.json();
}

export async function fetchLatestPolicySimulationApplication(
  tenantExternalId: string
): Promise<{
  application: {
    policyVersion: number | null;
    status: string;
    appliedAt: string;
  } | null;
}> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/policy-simulations/applications/latest`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`policy-simulation application failed: ${res.status}`);
  return res.json();
}

export interface ThresholdSuggestionResponse {
  sampleCount: number;
  suggestion: {
    allow: number;
    downgrade: number;
    challenge: number;
    revoke: number;
    fBeta: number;
    precision: number;
    recall: number;
  };
  /** Epic A: Expected Calibration Error of the fitted Platt model. */
  ece?: number;
  /** Epic A: raw band distribution over the same samples/thresholds (baseline). */
  rawBands?: Record<string, number>;
  /** Epic A: candidate calibrated band distribution (counts per action). */
  calibratedBands?: Record<string, number>;
}

export async function fetchThresholdSuggestions(
  tenantExternalId: string
): Promise<ThresholdSuggestionResponse> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/policy-simulations/threshold-suggestions`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`threshold-suggestions failed: ${res.status}`);
  return res.json();
}

/** Epic A — calibration model row (from /calibration/models). */
export interface CalibrationModelRow {
  id: number;
  tenantId: number;
  kind: "platt" | "isotonic";
  model: unknown;
  fittedFromOutcomeCount: number;
  fBeta: number | null;
  ece: number | null;
  suggestedThresholds: unknown | null;
  status: "shadow" | "promoted";
  createdAt: string;
  promotedAt: string | null;
}

export async function fetchCalibrationModels(
  tenantExternalId: string
): Promise<{ items: CalibrationModelRow[]; enforcementScore: "raw" | "calibrated" }> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/calibration/models`
  );
  if (!res.ok) throw new Error(`calibration models failed: ${res.status}`);
  return res.json() as Promise<{
    items: CalibrationModelRow[];
    enforcementScore: "raw" | "calibrated";
  }>;
}

/**
 * Epic A — lightweight fetch of the active (promoted) model + the tenant's
 * current `enforcementScore`. Used to show the enforcement state eagerly
 * without loading the full model list.
 */
export async function fetchActiveCalibrationModel(
  tenantExternalId: string
): Promise<{ model: CalibrationModelRow | null; enforcementScore: "raw" | "calibrated" }> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/calibration/models/active`
  );
  if (!res.ok) throw new Error(`active calibration model failed: ${res.status}`);
  return res.json() as Promise<{
    model: CalibrationModelRow | null;
    enforcementScore: "raw" | "calibrated";
  }>;
}

/**
 * Epic A (A6) — promote a shadow calibration model. Pass `enforce: true` to
 * flip the tenant's `enforcementScore` to "calibrated" (the cutover). Returns
 * the promoted model row + the resulting enforcement score.
 */
export async function promoteCalibrationModel(
  tenantExternalId: string,
  modelId: number,
  opts?: { enforce?: boolean }
): Promise<{ model: CalibrationModelRow; enforcementScore?: "calibrated" }> {
  const enforce = opts?.enforce ? "true" : "false";
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/calibration/models/${modelId}/promote?enforce=${enforce}`,
    { method: "POST" }
  );
  if (!res.ok) throw new Error(`promote calibration failed: ${res.status}`);
  return res.json() as Promise<{ model: CalibrationModelRow; enforcementScore?: "calibrated" }>;
}

/**
 * Epic A (A6) — rollback: demote the active calibration model and flip
 * `enforcementScore` back to "raw". Instant revert, no migration.
 */
export async function demoteCalibrationModel(
  tenantExternalId: string
): Promise<{ ok: boolean; enforcementScore: "raw" }> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/calibration/models/demote`,
    { method: "POST" }
  );
  if (!res.ok) throw new Error(`demote calibration failed: ${res.status}`);
  return res.json() as Promise<{ ok: boolean; enforcementScore: "raw" }>;
}

// ── Epic C (C3): self-tuning bot thresholds ──────────────────────────────────

/** Env-default bot-detection cutoffs (mirrors rules_engine.py defaults). */
export interface BotThresholdEnvDefaults {
  typing_speed: number;
  api_call_rate: number;
}

/** Epic C (C3) — bot-threshold model row (from /bot-thresholds/models). */
export interface BotThresholdModelRow {
  id: number;
  tenantId: number;
  thresholds: { typing_speed: number; api_call_rate: number };
  guardrails: Record<string, { min: number; max: number }> | null;
  fittedFromSampleCount: number;
  metrics: Record<string, number> | null;
  status: "shadow" | "promoted";
  createdAt: string;
  promotedAt: string | null;
}

export async function fetchBotThresholdModels(
  tenantExternalId: string
): Promise<{ items: BotThresholdModelRow[]; envDefaults: BotThresholdEnvDefaults }> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/bot-thresholds/models`
  );
  if (!res.ok) throw new Error(`bot-threshold models failed: ${res.status}`);
  return res.json() as Promise<{
    items: BotThresholdModelRow[];
    envDefaults: BotThresholdEnvDefaults;
  }>;
}

export async function fetchActiveBotThresholdModel(
  tenantExternalId: string
): Promise<{ model: BotThresholdModelRow | null; envDefaults: BotThresholdEnvDefaults }> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/bot-thresholds/models/active`
  );
  if (!res.ok) throw new Error(`active bot-threshold model failed: ${res.status}`);
  return res.json() as Promise<{
    model: BotThresholdModelRow | null;
    envDefaults: BotThresholdEnvDefaults;
  }>;
}

export async function fetchPromotedBotThresholdModel(
  tenantExternalId: string
): Promise<{ model: BotThresholdModelRow | null; envDefaults: BotThresholdEnvDefaults }> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/bot-thresholds/models/promoted`
  );
  if (!res.ok) throw new Error(`promoted bot-threshold model failed: ${res.status}`);
  return res.json() as Promise<{
    model: BotThresholdModelRow | null;
    envDefaults: BotThresholdEnvDefaults;
  }>;
}

/** Epic C (C3) — promote a shadow bot-threshold model so /decide enforces it. */
export async function promoteBotThresholdModel(
  tenantExternalId: string,
  modelId: number
): Promise<{ model: BotThresholdModelRow; envDefaults: BotThresholdEnvDefaults }> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/bot-thresholds/models/${modelId}/promote`,
    { method: "POST" }
  );
  if (!res.ok) throw new Error(`promote bot-threshold failed: ${res.status}`);
  return res.json() as Promise<{
    model: BotThresholdModelRow;
    envDefaults: BotThresholdEnvDefaults;
  }>;
}

/** Epic C (C3) — rollback: demote the promoted bot-threshold model to shadow. */
export async function demoteBotThresholdModel(
  tenantExternalId: string
): Promise<{ ok: boolean; envDefaults: BotThresholdEnvDefaults }> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/bot-thresholds/models/demote`,
    { method: "POST" }
  );
  if (!res.ok) throw new Error(`demote bot-threshold failed: ${res.status}`);
  return res.json() as Promise<{ ok: boolean; envDefaults: BotThresholdEnvDefaults }>;
}

export async function recordDecisionOutcome(
  tenantExternalId: string,
  body: {
    sessionId: string;
    identityExternalId: string;
    disposition:
      | "true_positive"
      | "false_positive"
      | "confirmed_incident"
      | "benign"
      | "inconclusive";
    decisionTraceId?: number;
    rawConfidenceScore?: number;
    recordedAction?: string;
    analystNotes?: string;
  }
): Promise<{ outcome: Record<string, unknown> }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/decision-outcomes`,
    { method: "POST", headers: headersJson(), body: JSON.stringify(body) }
  );
  if (!res.ok) throw new Error(`record decision-outcome failed: ${res.status}`);
  return res.json();
}

// ─── Epic B (B4): decision-outcome listing + label provenance ────────────────

export type DecisionOutcomeSource =
  | "analyst"
  | "soar"
  | "incident"
  | "helpdesk_reversal"
  | "system";

export interface DecisionOutcomeRow {
  id: number;
  sessionId: string;
  identityExternalId: string;
  disposition: string;
  rawConfidenceScore: number | null;
  recordedAction: string | null;
  analystNotes: string | null;
  recordedBy: string | null;
  source: DecisionOutcomeSource;
  createdAt: string;
}

export async function fetchDecisionOutcomes(
  tenantExternalId: string,
  opts: { limit?: number; sinceDays?: number; source?: DecisionOutcomeSource } = {}
): Promise<{ items: DecisionOutcomeRow[] }> {
  const params = new URLSearchParams();
  if (opts.limit) params.set("limit", String(opts.limit));
  if (opts.sinceDays) params.set("sinceDays", String(opts.sinceDays));
  if (opts.source) params.set("source", opts.source);
  const qs = params.toString() ? `?${params.toString()}` : "";
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/decision-outcomes${qs}`
  );
  if (!res.ok) throw new Error(`fetch decision-outcomes failed: ${res.status}`);
  return res.json();
}

export interface LabeledTrainingSample {
  sessionId: string;
  identityExternalId: string;
  rawScore: number;
  label: boolean;
  disposition: string;
  source: DecisionOutcomeSource;
  createdAt: string;
}

export async function fetchLabeledSamples(
  tenantExternalId: string,
  opts: { limit?: number; sinceDays?: number; source?: DecisionOutcomeSource } = {}
): Promise<{ items: LabeledTrainingSample[] }> {
  const params = new URLSearchParams();
  if (opts.limit) params.set("limit", String(opts.limit));
  if (opts.sinceDays) params.set("sinceDays", String(opts.sinceDays));
  if (opts.source) params.set("source", opts.source);
  const qs = params.toString() ? `?${params.toString()}` : "";
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/decision-outcomes/labeled-samples${qs}`
  );
  if (!res.ok) throw new Error(`fetch labeled-samples failed: ${res.status}`);
  return res.json();
}

// ─── Epic B (B4): label ingestion (SOAR / incident / helpdesk reversal) ──────

export async function ingestSoarLabel(
  tenantExternalId: string,
  body: {
    sessionId: string;
    identityExternalId: string;
    confirmedMalicious: boolean;
    rawConfidenceScore?: number;
    recordedAction?: string;
    playbookRunId?: string;
    analystNotes?: string;
  }
): Promise<unknown> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/label-ingestion/soar`,
    { method: "POST", headers: headersJson(), body: JSON.stringify(body) }
  );
  if (!res.ok) throw new Error(`ingest SOAR label failed: ${res.status}`);
  return res.json();
}

export async function ingestIncidentLabel(
  tenantExternalId: string,
  body: {
    sessionId: string;
    identityExternalId: string;
    severity?: string;
    rawConfidenceScore?: number;
    recordedAction?: string;
    incidentExternalId?: string;
    analystNotes?: string;
  }
): Promise<unknown> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/label-ingestion/incident`,
    { method: "POST", headers: headersJson(), body: JSON.stringify(body) }
  );
  if (!res.ok) throw new Error(`ingest incident label failed: ${res.status}`);
  return res.json();
}

export async function ingestHelpdeskReversalLabel(
  tenantExternalId: string,
  body: {
    sessionId: string;
    identityExternalId: string;
    rawConfidenceScore?: number;
    recordedAction?: string;
    ticketExternalId?: string;
    analystNotes?: string;
    recordedBy?: string;
  }
): Promise<unknown> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/label-ingestion/helpdesk-reversal`,
    { method: "POST", headers: headersJson(), body: JSON.stringify(body) }
  );
  if (!res.ok) throw new Error(`ingest helpdesk reversal label failed: ${res.status}`);
  return res.json();
}

// ─── Epic B (B5): model provenance + drift status (via gateway proxy) ────────

export interface ModelStatusVector {
  vector: string;
  state: string;
  model_path: string | null;
  model_present_on_disk: boolean;
}

export interface ModelStatus {
  status: string;
  overall: string;
  model_dir: string;
  vectors: ModelStatusVector[];
  provenance: string;
  semi_supervised_loaded: boolean;
  manifest: Record<string, unknown> | null;
}

export interface DriftFeaturePsi {
  [feature: string]: number;
}

export interface DriftStatus {
  status: string;
  drift: "none" | "moderate" | "significant" | "unknown";
  psi: number | null;
  features: DriftFeaturePsi;
  live_sample_count?: number;
  manifest_kind?: string;
  autonomy_recommendation: "maintain" | "watch" | "throttle";
  reason?: string;
  error?: string;
}

export async function fetchModelStatus(
  tenantExternalId: string
): Promise<ModelStatus> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/ml/model-status`
  );
  if (!res.ok) throw new Error(`fetch model-status failed: ${res.status}`);
  return res.json();
}

export async function fetchDriftStatus(
  tenantExternalId: string
): Promise<DriftStatus> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/ml/drift-status`
  );
  if (!res.ok) throw new Error(`fetch drift-status failed: ${res.status}`);
  return res.json();
}

// ─── Browser-native ephemeral access (Innovation Bet #12) ─────────────────────

export interface BrowserGrantRow {
  grantId: string;
  identityExternalId: string;
  target: string;
  singleUse: boolean;
  issuedAt: string;
  expiresAt: string;
  consumedAt: string | null;
  revokedAt: string | null;
}

export interface IssuedBrowserGrant {
  grantId: string;
  token: string;
  target: string;
  expiresAt: string;
  launchUrl?: string;
}

export async function issueBrowserGrant(
  tenantExternalId: string,
  body: { identityExternalId: string; target: string; ttlSeconds?: number; singleUse?: boolean }
): Promise<IssuedBrowserGrant> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/browser-grants`,
    { method: "POST", headers: headersJson(), body: JSON.stringify(body) }
  );
  if (!res.ok) throw new Error(`issue browser grant failed: ${res.status}`);
  return res.json();
}

export async function fetchBrowserGrants(
  tenantExternalId: string
): Promise<{ items: BrowserGrantRow[]; total: number }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/browser-grants`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`browser grants failed: ${res.status}`);
  return res.json();
}

export async function revokeBrowserGrant(
  tenantExternalId: string,
  grantId: string
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/browser-grants/${encodeURIComponent(grantId)}`,
    { method: "DELETE", headers: headersJson() }
  );
  if (!res.ok) throw new Error(`revoke browser grant failed: ${res.status}`);
}

// ─── Federated threat-intel PSI overlap (Innovation Bet #10) ──────────────────

export interface CommunityOverlapResult {
  submitted: number;
  poolSize: number;
  matchCount: number;
  sharedIndicators: string[];
}

export interface PsiPeer {
  peerId: string;
  name: string;
  peerTenantId: string;
  peerBaseUrl?: string | null;
  isActive: boolean;
}

export interface PsiExchangeRecord {
  id: number;
  direction: string;
  peerId: string | null;
  peerTenantId: string;
  exchangeId: string;
  initiatorSetSize: number;
  responderSetSize: number;
  matchCount: number;
  status: string;
  startedAt: string;
  completedAt: string | null;
}

export interface PeerOverlapResult {
  exchangeId: string;
  peerId: string;
  peerTenantId: string;
  submitted: number;
  peerSetSize: number;
  matchCount: number;
  sharedIndicators: string[];
}

export async function fetchPsiPeers(
  tenantExternalId: string
): Promise<{ items: PsiPeer[]; total: number }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/threat-intel/peers`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`psi peers failed: ${res.status}`);
  return res.json();
}

export async function createPsiPeer(
  tenantExternalId: string,
  body: { name: string; peerTenantId: string; publicKeyPem: string; peerBaseUrl?: string }
): Promise<PsiPeer> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/threat-intel/peers`,
    { method: "POST", headers: headersJson(), body: JSON.stringify(body) }
  );
  if (!res.ok) throw new Error(`create psi peer failed: ${res.status}`);
  return res.json();
}

export async function deletePsiPeer(
  tenantExternalId: string,
  peerId: string
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/threat-intel/peers/${encodeURIComponent(peerId)}`,
    { method: "DELETE", headers: headersJson() }
  );
  if (!res.ok) throw new Error(`delete psi peer failed: ${res.status}`);
}

/**
 * Privacy-preserving overlap of the operator's indicators with the community
 * pool. Raw indicators are hashed before any comparison; only the operator's own
 * matched indicators are returned.
 */
export async function runCommunityOverlap(
  tenantExternalId: string,
  indicators: string[]
): Promise<CommunityOverlapResult> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/threat-intel/overlap/community`,
    { method: "POST", headers: headersJson(), body: JSON.stringify({ indicators }) }
  );
  if (!res.ok) throw new Error(`community overlap failed: ${res.status}`);
  return res.json();
}

export async function runPeerPsiExchange(
  tenantExternalId: string,
  peerId: string,
  indicators: string[]
): Promise<PeerOverlapResult> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/threat-intel/overlap/peer/${encodeURIComponent(peerId)}`,
    { method: "POST", headers: headersJson(), body: JSON.stringify({ indicators }) }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? `peer overlap failed: ${res.status}`);
  }
  return res.json();
}

export async function fetchPsiExchanges(
  tenantExternalId: string
): Promise<{ items: PsiExchangeRecord[]; total: number }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/threat-intel/exchanges`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`psi exchanges failed: ${res.status}`);
  return res.json();
}

// ─── CAEP / Shared Signals receivers (Innovation Bet #1) ──────────────────────

export interface SsfReceiver {
  receiverId: string;
  name: string;
  deliveryUrl: string;
  audience: string;
  eventTypes: string[];
  isActive: boolean;
}

export async function fetchSsfReceivers(
  tenantExternalId: string
): Promise<{ items: SsfReceiver[]; total: number }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/ssf/receivers`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`ssf receivers failed: ${res.status}`);
  return res.json();
}

export async function createSsfReceiver(
  tenantExternalId: string,
  body: { name: string; deliveryUrl: string; audience: string; authToken?: string; eventTypes: string[] }
): Promise<SsfReceiver> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/ssf/receivers`,
    { method: "POST", headers: headersJson(), body: JSON.stringify(body) }
  );
  if (!res.ok) throw new Error(`create ssf receiver failed: ${res.status}`);
  return res.json();
}

export async function deleteSsfReceiver(
  tenantExternalId: string,
  receiverId: string
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/ssf/receivers/${encodeURIComponent(receiverId)}`,
    { method: "DELETE", headers: headersJson() }
  );
  if (!res.ok) throw new Error(`delete ssf receiver failed: ${res.status}`);
}

// ─── SSF trusted transmitters + inbound signal feed (Innovation Bet #1) ───────

export interface SsfTransmitter {
  transmitterId: string;
  name: string;
  issuer: string;
  audience: string;
  alg: string;
  isActive: boolean;
}

export interface SsfReceivedEvent {
  id: number;
  issuer: string;
  jti: string;
  eventType: string;
  subject: string | null;
  status: string;
  action: string | null;
  receivedAt: string;
}

export async function fetchSsfTransmitters(
  tenantExternalId: string
): Promise<{ items: SsfTransmitter[]; total: number }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/ssf/transmitters`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`ssf transmitters failed: ${res.status}`);
  return res.json();
}

export async function createSsfTransmitter(
  tenantExternalId: string,
  body: {
    name: string;
    issuer: string;
    audience: string;
    alg: "HS256" | "EdDSA";
    sharedSecret?: string;
    publicKeyPem?: string;
  }
): Promise<SsfTransmitter> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/ssf/transmitters`,
    { method: "POST", headers: headersJson(), body: JSON.stringify(body) }
  );
  if (!res.ok) throw new Error(`create ssf transmitter failed: ${res.status}`);
  return res.json();
}

export async function deleteSsfTransmitter(
  tenantExternalId: string,
  transmitterId: string
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/ssf/transmitters/${encodeURIComponent(transmitterId)}`,
    { method: "DELETE", headers: headersJson() }
  );
  if (!res.ok) throw new Error(`delete ssf transmitter failed: ${res.status}`);
}

export async function fetchSsfReceivedEvents(
  tenantExternalId: string
): Promise<{ items: SsfReceivedEvent[]; total: number }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/ssf/received-events`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`ssf received-events failed: ${res.status}`);
  return res.json();
}

// ─── Agentic trust broker — intents (Innovation Bet #2) ───────────────────────

export interface AgentIntent {
  intentId: string;
  identityExternalId: string;
  allowedScopes: string[];
  allowedResources: string[];
  maxActionsPerMinute: number | null;
  status: string;
  expiresAt: string | null;
  /** IA-issued SPIFFE workload identity (Innovation Bet #2), once minted. */
  spiffeId?: string | null;
}

export interface AgentSvidResult {
  spiffeId: string;
  svid: string;
  alg: string;
  keyId: string;
  issuer: string;
  audience: string;
  expiresAt: string;
  publicKeyPem: string;
}

/** Mints an IA-issued SPIFFE JWT-SVID for an agent intent (Innovation Bet #2). */
export async function mintAgentSvid(
  tenantExternalId: string,
  intentId: string,
  body?: { audience?: string }
): Promise<AgentSvidResult> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/agent-intents/${encodeURIComponent(intentId)}/svid`,
    { method: "POST", headers: headersJson(), body: JSON.stringify(body ?? {}) }
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    const detail = body?.error?.trim();
    throw new Error(detail ? `mint SVID failed: ${detail}` : `mint SVID failed: ${res.status}`);
  }
  return res.json();
}

export async function fetchAgentIntents(
  tenantExternalId: string
): Promise<{ items: AgentIntent[]; total: number }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/agent-intents`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`agent intents failed: ${res.status}`);
  return res.json();
}

export async function revokeAgentIntent(
  tenantExternalId: string,
  intentId: string
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/agent-intents/${encodeURIComponent(intentId)}`,
    { method: "DELETE", headers: headersJson() }
  );
  if (!res.ok) throw new Error(`revoke agent intent failed: ${res.status}`);
}

/**
 * S1: marks an agent task complete and tears down its bound ephemeral
 * credential (vault tokens + Postgres session). Idempotent.
 */
export async function completeAgentIntent(
  tenantExternalId: string,
  intentId: string,
  body?: { reason?: string }
): Promise<{ intentId: string; status: string; tokensRevoked: number; idempotent: boolean }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/agent-intents/${encodeURIComponent(intentId)}/complete`,
    { method: "POST", headers: headersJson(), body: JSON.stringify(body ?? {}) }
  );
  if (!res.ok) {
    const detail = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(
      detail?.error?.trim() ? `complete agent intent failed: ${detail.error}` : `complete agent intent failed: ${res.status}`
    );
  }
  return res.json();
}

// ─── Signing keys / trust anchors (Innovation Bet #4) ─────────────────────────

export interface SigningKeyInfo {
  domain: string;
  alg: string;
  keyId: string;
  asymmetric: boolean;
  publicKeyPem?: string;
}

export async function fetchSigningKeys(): Promise<{ keys: SigningKeyInfo[]; note: string }> {
  const res = await fetch(`${API_BASE}/signing-keys`, { headers: headersJson() });
  if (!res.ok) throw new Error(`signing-keys failed: ${res.status}`);
  return res.json();
}

export async function fetchJwks(): Promise<{ keys: Array<Record<string, unknown>> }> {
  const res = await fetch(`${API_BASE}/signing-keys/jwks`, { headers: headersJson() });
  if (!res.ok) throw new Error(`jwks failed: ${res.status}`);
  return res.json();
}

// ─── Verifiable revocation receipts (Innovation Bet #3) ───────────────────────

export interface RevocationReceiptRow {
  id: number;
  receiptId: string;
  subjectKind: string;
  tokenId: string | null;
  sessionId: string;
  reason: string;
  initiatedBy: string | null;
  revokedAt: string;
  alg: string;
  keyId: string;
  /** Sprint S2 — digest of the previous receipt in the tenant chain (null = head). */
  previousReceiptDigest: string | null;
  receipt: unknown;
  createdAt: string;
}

export interface ReceiptVerifyResult {
  receiptId: string;
  valid: boolean;
  reason?: string;
  alg: string;
  keyId: string;
}

/** Sprint S2 — an ordered chain slice ending at a receipt (oldest → newest). */
export interface ReceiptChainItem {
  receiptId: string;
  previousReceiptDigest: string | null;
  revokedAt: string;
  receipt: unknown;
}

export interface ReceiptChainSlice {
  receiptId: string;
  length: number;
  items: ReceiptChainItem[];
}

/** Sprint S2 — JWKS document for receipt verification (EdDSA mode). */
export interface ReceiptJwks {
  keys: Array<{ kty: string; crv: string; kid: string; alg: string; use: string; x?: string }>;
}

export async function fetchRevocationReceipts(
  tenantExternalId: string,
  params?: { limit?: number; offset?: number }
): Promise<{ items: RevocationReceiptRow[]; total: number }> {
  const qs = new URLSearchParams();
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  const q = qs.toString();
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/revocation-receipts${q ? `?${q}` : ""}`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`revocation-receipts failed: ${res.status}`);
  return res.json();
}

/** Bulk auditor export of revocation receipts as NDJSON text (Innovation Bet #3). */
export async function exportRevocationReceiptsNdjson(
  tenantExternalId: string,
  opts?: { from?: string; to?: string; limit?: number }
): Promise<string> {
  const qs = new URLSearchParams();
  if (opts?.from) qs.set("from", opts.from);
  if (opts?.to) qs.set("to", opts.to);
  if (opts?.limit != null) qs.set("limit", String(opts.limit));
  const q = qs.toString();
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/revocation-receipts/export${q ? `?${q}` : ""}`
  );
  if (!res.ok) throw new Error(`receipts export failed: ${res.status}`);
  return res.text();
}

export async function verifyRevocationReceiptById(
  tenantExternalId: string,
  receiptId: string
): Promise<ReceiptVerifyResult> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/revocation-receipts/${encodeURIComponent(receiptId)}/verify`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`verify receipt failed: ${res.status}`);
  return res.json();
}

export async function verifySuppliedReceipt(
  tenantExternalId: string,
  receipt: unknown
): Promise<{ valid: boolean; reason?: string }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/revocation-receipts/verify`,
    { method: "POST", headers: headersJson(), body: JSON.stringify(receipt) }
  );
  if (!res.ok) throw new Error(`verify supplied receipt failed: ${res.status}`);
  return res.json();
}

/** Sprint S2 — fetch the JWKS-formatted verifying key for offline receipt verification. */
export async function fetchReceiptJwks(): Promise<ReceiptJwks> {
  const res = await fetch(`${API_BASE}/revocation-receipts/jwks`, { headers: headersJson() });
  if (!res.ok) throw new Error(`receipt jwks failed: ${res.status}`);
  return res.json();
}

/** Sprint S2 — fetch an ordered chain slice ending at a receipt. */
export async function fetchReceiptChain(
  tenantExternalId: string,
  receiptId: string,
  maxDepth?: number
): Promise<ReceiptChainSlice> {
  const qs = new URLSearchParams();
  if (maxDepth != null) qs.set("maxDepth", String(maxDepth));
  const q = qs.toString();
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/revocation-receipts/${encodeURIComponent(receiptId)}/chain${q ? `?${q}` : ""}`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`receipt chain failed: ${res.status}`);
  return res.json();
}

/** Sprint S2 — verify a caller-supplied chain (signatures + linkage). */
export async function verifyReceiptChain(
  tenantExternalId: string,
  receipts: unknown[]
): Promise<{ valid: boolean; reason?: string; index?: number }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/revocation-receipts/verify-chain`,
    { method: "POST", headers: headersJson(), body: JSON.stringify(receipts) }
  );
  if (!res.ok) throw new Error(`verify receipt chain failed: ${res.status}`);
  return res.json();
}

// ─── Agentic SOC (E14) ───────────────────────────────────────────────────────

export interface AgenticSocSummary {
  tenantExternalId: string;
  generatedAt: string;
  mcpServerEnabled: boolean;
  networkReevalEnabled: boolean;
  recentItdrCount: number;
  byotQueueDepth: number;
  threatIntelFeeds: string[];
  lastThreatSyncAt: string | null;
}

export async function fetchAgenticSocSummary(
  tenantExternalId: string
): Promise<AgenticSocSummary> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/agentic-soc/summary`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`agentic-soc summary failed: ${res.status}`);
  return res.json();
}

// ─── Agentic broker — declare + evaluate (Innovation Bet #2) ──────────────────

export async function declareAgentIntent(
  tenantExternalId: string,
  body: {
    identityExternalId: string;
    allowedScopes: string[];
    allowedResources: string[];
    toolPolicy?: { allowedTools?: string[]; toolBudgets?: Record<string, number>; requireApprovalTools?: string[] };
    maxActionsPerMinute?: number;
    expiresAt?: string;
  }
): Promise<AgentIntent> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/agent-intents`,
    { method: "POST", headers: headersJson(), body: JSON.stringify(body) }
  );
  if (!res.ok) throw new Error(`declare intent failed: ${res.status}`);
  return res.json();
}

export async function evaluateAgentAction(
  tenantExternalId: string,
  intentId: string,
  body: { scope: string; resource: string; actionsPerMinute?: number }
): Promise<{
  intentId: string;
  driftScore: number;
  withinIntent: boolean;
  decision: string;
  reasons: string[];
  enforced: boolean;
  tokensRevoked: number;
  /** S1: pinned-model semantic cosine-delta score (0-100). */
  embeddingScore?: number;
  embeddingModel?: string;
  embeddingModelVersion?: string;
}> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/agent-intents/${encodeURIComponent(intentId)}/evaluate`,
    { method: "POST", headers: headersJson(), body: JSON.stringify(body) }
  );
  if (!res.ok) throw new Error(`evaluate action failed: ${res.status}`);
  return res.json();
}

// ── NHI inventory (Innovation Bet #7, Phase 8) ──────────────────────────────

export interface NhiInventoryItem {
  nhiId: string;
  kind: string;
  source: string;
  ageDays: number;
  daysSinceRotation: number | null;
  daysSinceLastUse: number | null;
  privileged: boolean;
  widelyShared: boolean;
  ephemeralEligible: boolean;
  riskScore: number;
  band: string;
  recommendation: string;
  reasons: string[];
  discoveredAt: string;
  updatedAt: string;
  // Enrichment columns (may be absent on older gateways).
  lifecycleState?: string;
  workloadIdentity?: string | null;
  expiresAt?: string | null;
  fingerprint?: string | null;
  cloudAccount?: string | null;
  region?: string | null;
  sourceArn?: string | null;
  tags?: Record<string, unknown>;
}

export interface NhiInventorySummary {
  total: number;
  byBand: Record<string, number>;
  migrationCandidates: number;
}

// ── NHI per-identity context (decision support) ─────────────────────────────

export interface NhiReasonDetail {
  code: string;
  label: string;
  description: string;
  action?: string;
  severity: "info" | "low" | "medium" | "high";
}

export interface NhiContext {
  nhi: NhiInventoryItem & { reasonDetails: NhiReasonDetail[] };
  ownership: {
    ownerIdentityId: string | null;
    ownerTeam: string | null;
    applicationId: string | null;
    verifiedBy: string | null;
    verifiedAt: string | null;
  } | null;
  lifecycleAudit: Array<{
    fromState: string;
    toState: string;
    actor: string;
    reason: string;
    occurredAt: string;
  }>;
  activity: {
    principalArn: string | null;
    last30dEventCount: number;
    recent: Array<{
      eventTime: string;
      eventSource: string;
      eventName: string;
      awsRegion: string | null;
      sourceIp: string | null;
    }>;
  };
  ciemFindings: Array<{
    recommendationType: string;
    severity: string | null;
    summary: string | null;
    status: string;
    riskScore: number | null;
    createdAt: string;
  }>;
  workloadLink: {
    spiffeId: string;
    intentId: string;
    agentIdentityExternalId: string;
    status: string;
  } | null;
  /** Live governance context, present only for ai_agent NHIs. */
  agent: {
    identityExternalId: string;
    intents: Array<{
      intentId: string;
      status: string;
      scopes: string[];
      resources: string[];
      expiresAt: string | null;
    }>;
    activeIntentCount: number;
    latestDriftScore: number | null;
    anomalies: Array<{ anomalyScore: number; signals: string[]; occurredAt: string }>;
    pendingApprovals: Array<{
      requestId: string;
      intentId: string;
      tool: string | null;
      scope: string;
      driftScore: number;
      requestedAt: string;
    }>;
    delegationChains: Array<{
      chainId: string;
      depth: number;
      maxDepth: number;
      status: string;
      role: "originator" | "current";
    }>;
  } | null;
}

export async function fetchNhiContext(
  tenantExternalId: string,
  nhiId: string
): Promise<NhiContext> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/nhi/inventory/${encodeURIComponent(
      nhiId
    )}/context`,
    { headers: headersJson() }
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `nhi context failed: ${res.status}`);
  }
  return res.json();
}

// ── NHI posture rollup ──────────────────────────────────────────────────────

export interface NhiPostureFinding {
  id: string;
  title: string;
  severity: "low" | "medium" | "high";
  count: number;
  recommendation: string;
}

export interface NhiPostureSummary {
  total: number;
  postureScore: number;
  postureBand: "strong" | "fair" | "weak" | "critical";
  avgRiskScore: number;
  byBand: Record<string, number>;
  bySource: Record<string, number>;
  byKind: Record<string, number>;
  hygiene: {
    neverRotated: number;
    staleRotation: number;
    dormant: number;
    usageUnknown: number;
    privileged: number;
    widelyShared: number;
    orphaned: number;
    migrationCandidates: number;
    privilegedUnrotated: number;
    dormantPrivileged: number;
  };
  topFindings: NhiPostureFinding[];
}

export interface NhiPostureResponse {
  posture: NhiPostureSummary;
  coverage: Array<{
    source: string;
    configured: boolean;
    status: string;
    discoveredCount: number;
  }>;
  trend: { current: number | null; previous: number | null; delta: number | null };
}

export async function fetchNhiPosture(tenantExternalId: string): Promise<NhiPostureResponse> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/nhi/posture`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`nhi posture failed: ${res.status}`);
  return res.json();
}

/**
 * Sprint 5A — on-demand identity risk-score backfill (platform-admin only).
 * Triggers `IdentityGovernanceRiskWorker.runAll()` so the dashboard's "unknown"
 * risk bucket shrinks immediately. Returns the number of tenants scored.
 */
export async function triggerIdentityRiskBackfill(): Promise<{ scored: number; timestamp: string }> {
  const res = await fetch(`${API_BASE}/admin/identity-risk-backfill`, {
    method: "POST",
    headers: headersJson(),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(
      (body as { error?: string }).error ?? `identity-risk-backfill failed: ${res.status}`
    );
  }
  return res.json();
}

export async function fetchNhiInventory(
  tenantExternalId: string,
  band?: string
): Promise<{ summary: NhiInventorySummary; items: NhiInventoryItem[] }> {
  const qs = band ? `?band=${encodeURIComponent(band)}` : "";
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/nhi/inventory${qs}`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`nhi inventory failed: ${res.status}`);
  return res.json();
}

export async function discoverNhiInventory(
  tenantExternalId: string
): Promise<{ discovered: number; summary: NhiInventorySummary }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/nhi/inventory/discover`,
    { method: "POST", headers: headersJson() }
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { detail?: string }).detail ?? `nhi discovery failed: ${res.status}`);
  }
  return res.json();
}

export interface K8sClusterDiscoveryResult {
  id: string;
  name: string;
  ok: boolean;
  count: number;
  error?: string;
}

export async function discoverK8sNhiInventory(
  tenantExternalId: string
): Promise<{
  discovered: number;
  summary: NhiInventorySummary;
  source?: string;
  perCluster?: K8sClusterDiscoveryResult[];
  warnings?: Array<{ cluster: string; detail: string }>;
}> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/nhi/inventory/discover-k8s`,
    { method: "POST", headers: headersJson() }
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(
      (body as { detail?: string }).detail ?? `k8s nhi discovery failed: ${res.status}`
    );
  }
  return res.json();
}

/** Discovers governed AI agents as NHIs (internal source, behavioral scoring). */
export async function discoverAgentNhiInventory(
  tenantExternalId: string
): Promise<{ discovered: number; summary: NhiInventorySummary; source: string }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/nhi/inventory/discover-agents`,
    { method: "POST", headers: headersJson() }
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(
      (body as { detail?: string; error?: string }).detail ??
        (body as { error?: string }).error ??
        `agent nhi discovery failed: ${res.status}`
    );
  }
  return res.json();
}

/** S3: orchestrated discovery across every enabled connector in one run. */
export async function discoverAllNhiInventory(
  tenantExternalId: string
): Promise<{ discovered: number; unique: number; summary: NhiInventorySummary; errors: Array<{ source: string; error: string }> }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/nhi/inventory/discover-all`,
    { method: "POST", headers: headersJson() }
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(
      (body as { error?: string }).error ?? `orchestrated nhi discovery failed: ${res.status}`
    );
  }
  return res.json();
}

/** S3: one-click static→ephemeral migration for an eligible NHI. */
export async function migrateNhiToEphemeral(
  tenantExternalId: string,
  nhiId: string,
  input?: { identityExternalId?: string; leaseTtlSeconds?: number }
): Promise<{ status: string; requestId: number; leaseExternalId: string | null }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/nhi/inventory/${encodeURIComponent(nhiId)}/migrate`,
    { method: "POST", headers: headersJson(), body: JSON.stringify(input ?? {}) }
  );
  const body = (await res.json().catch(() => ({}))) as {
    error?: string;
    status?: string;
    requestId?: number;
    leaseExternalId?: string | null;
  };
  if (!res.ok) {
    throw new Error(body.error ?? `nhi migration failed: ${res.status}`);
  }
  return {
    status: body.status ?? "unknown",
    requestId: body.requestId ?? 0,
    leaseExternalId: body.leaseExternalId ?? null,
  };
}

/** Transition an NHI to a new lifecycle state (used by the inventory drawer). */
export async function transitionNhi(
  tenantExternalId: string,
  nhiId: string,
  input: { fromState: string; toState: string; actor?: string; reason?: string }
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/nhi/inventory/${encodeURIComponent(
      nhiId
    )}/transition`,
    {
      method: "POST",
      headers: headersJson(),
      body: JSON.stringify({
        fromState: input.fromState,
        toState: input.toState,
        actor: input.actor ?? "dashboard-operator",
        reason: input.reason ?? "Manual transition via dashboard",
      }),
    }
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `nhi transition failed: ${res.status}`);
  }
}

/** Create a remediation request (rotate / migrate / revoke) for an NHI. */
export async function createNhiRemediation(
  tenantExternalId: string,
  input: {
    nhiId: string;
    action: string;
    priority?: string;
    initiatedBy?: string;
    requiresApproval?: boolean;
  }
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/nhi/remediation`,
    {
      method: "POST",
      headers: headersJson(),
      body: JSON.stringify({
        nhiId: input.nhiId,
        action: input.action,
        priority: input.priority ?? "medium",
        initiatedBy: input.initiatedBy ?? "dashboard-operator",
        requiresApproval: input.requiresApproval ?? false,
      }),
    }
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `nhi remediation failed: ${res.status}`);
  }
}

export async function syncThreatIntelFeeds(input?: {
  limitPerFeed?: number;
}): Promise<{ syncedAt: string; results: Array<{ source: string; fetched: number; upserted: number; errors: string[] }> }> {
  const res = await fetch(`${API_BASE}/threat-intel/sync`, {
    method: "POST",
    headers: headersJson(),
    body: JSON.stringify({ limitPerFeed: input?.limitPerFeed ?? 100 }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `threat intel sync failed: ${res.status}`);
  }
  return res.json();
}

// ── Attack-path what-if (Innovation Bet #8, Phase 8) ────────────────────────

export interface AttackPathRecommendation {
  edgeId: string;
  from: string;
  to: string;
  kind?: string;
  targetsEliminated: number;
}

export interface AttackPathWhatIf {
  sources: number;
  targets: number;
  totalEdges: number;
  consideredEdges: number;
  truncated: boolean;
  approximated?: boolean;
  rankTimeoutMs?: number;
  recommendations: AttackPathRecommendation[];
}

export async function fetchAttackPathWhatIf(
  tenantExternalId: string,
  opts?: { targetTypes?: string[]; sourceTypes?: string[] }
): Promise<AttackPathWhatIf> {
  const qs = new URLSearchParams();
  if (opts?.targetTypes?.length) qs.set("targetTypes", opts.targetTypes.join(","));
  if (opts?.sourceTypes?.length) qs.set("sourceTypes", opts.sourceTypes.join(","));
  const q = qs.toString();
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/graph/what-if${q ? `?${q}` : ""}`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`what-if failed: ${res.status}`);
  return res.json();
}

export interface AttackPathSimulation {
  reachableBefore: string[];
  reachableAfter: string[];
  eliminatedTargets: string[];
  eliminatedFraction: number;
  prunedEdgeIds: string[];
}

export async function simulateAttackPathPruning(
  tenantExternalId: string,
  candidateEphemeralEdgeIds: string[]
): Promise<AttackPathSimulation> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/graph/what-if/simulate`,
    { method: "POST", headers: headersJson(), body: JSON.stringify({ candidateEphemeralEdgeIds }) }
  );
  if (!res.ok) throw new Error(`simulate failed: ${res.status}`);
  return res.json();
}

// ── Attack-path blast-radius + recommendations read path (Sprint S5) ─────────

export interface StandingPrivilegeCohortImpact {
  edgeId: string;
  from: string;
  to: string;
  kind?: string;
  targetsEliminatedIfEphemeral: number;
}

export interface BlastRadiusComparison {
  sources: number;
  targets: number;
  totalEdges: number;
  standingReachable: string[];
  ephemeralReachable: string[];
  eliminatedTargets: string[];
  eliminatedFraction: number;
  prunedEdgeIds: string[];
  byStandingPrivilegeCohort: StandingPrivilegeCohortImpact[];
}

export async function fetchBlastRadiusComparison(
  tenantExternalId: string
): Promise<BlastRadiusComparison> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/graph/blast-radius`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`blast-radius failed: ${res.status}`);
  return res.json();
}

export interface AttackPathRecommendationRow extends AttackPathRecommendation {
  computedAt: string;
}

export async function fetchAttackPathRecommendations(
  tenantExternalId: string
): Promise<{ stale: boolean; recommendations: AttackPathRecommendationRow[] }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/graph/recommendations`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`recommendations failed: ${res.status}`);
  return res.json();
}

// ── Agentic broker: authorize (PEP) + approval queue (Innovation Bet #2, Tier 1) ──

export interface AgentAuthorizeResult {
  decision: "allow" | "deny" | "step_up";
  driftScore: number;
  deviationScore?: number;
  toolAllowed: boolean;
  needsApproval: boolean;
  inputSeverity?: "none" | "suspicious" | "dangerous";
  reasons: string[];
  approvalRequestId?: string;
  enforced?: boolean;
  tokensRevoked?: number;
}

export async function authorizeAgentAction(
  tenantExternalId: string,
  intentId: string,
  body: {
    scope: string;
    resource: string;
    tool?: string;
    toolActionsPerMinute?: number;
    args?: Record<string, unknown>;
  }
): Promise<AgentAuthorizeResult> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/agent-intents/${encodeURIComponent(intentId)}/authorize`,
    { method: "POST", headers: headersJson(), body: JSON.stringify(body) }
  );
  if (!res.ok) throw new Error(`authorize failed: ${res.status}`);
  return res.json();
}

export interface AgentApproval {
  requestId: string;
  intentId: string;
  scope: string;
  resource: string;
  tool: string | null;
  driftScore: number;
  reasons: string[];
  status: string;
  requestedAt: string;
  expiresAt: string;
}

export async function fetchAgentApprovals(
  tenantExternalId: string
): Promise<{ items: AgentApproval[]; total: number }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/agent-intents/approvals`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`approvals failed: ${res.status}`);
  return res.json();
}

export async function decideAgentApproval(
  tenantExternalId: string,
  requestId: string,
  decision: "approve" | "deny"
): Promise<{ requestId: string; status: string }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/agent-intents/approvals/${encodeURIComponent(requestId)}/decide`,
    { method: "POST", headers: headersJson(), body: JSON.stringify({ decision }) }
  );
  if (!res.ok) throw new Error(`decide failed: ${res.status}`);
  return res.json();
}

// ── Agent fleet registry (Innovation Bet #2, Tier 1 — inventory) ─────────────

export interface AgentRegistryEntry {
  identityExternalId: string;
  intentCount: number;
  activeIntentCount: number;
  scopes: string[];
  resources: string[];
  tools: string[];
  lastSeen: string | null;
  riskBand: "normal" | "elevated";
  baseline: {
    actionCount: number;
    toolsObserved: number;
    scopesObserved: number;
    resourcesObserved: number;
    established: boolean;
  } | null;
}

export interface AgentRegistrySummary {
  totalAgents: number;
  totalIntents: number;
  elevated: number;
  baselined: number;
}

export async function fetchAgentRegistry(
  tenantExternalId: string
): Promise<{ summary: AgentRegistrySummary; agents: AgentRegistryEntry[] }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/agents`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`agent registry failed: ${res.status}`);
  return res.json();
}

// ── Tier 3 — fleet kill-switch / break-glass freeze + bulk revoke ────────────

export interface FleetFreezeState {
  frozen: boolean;
  reason: string | null;
  actor: string | null;
  frozenAt: string | null;
}

export async function fetchAgentFreezeState(tenantExternalId: string): Promise<FleetFreezeState> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/agents/freeze-state`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`freeze-state failed: ${res.status}`);
  return res.json();
}

export async function setAgentFreeze(
  tenantExternalId: string,
  frozen: boolean,
  body: { reason?: string; actor?: string } = {}
): Promise<FleetFreezeState> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/agents/${frozen ? "freeze" : "unfreeze"}`,
    { method: "POST", headers: headersJson(), body: JSON.stringify(body) }
  );
  if (!res.ok) throw new Error(`${frozen ? "freeze" : "unfreeze"} failed: ${res.status}`);
  return res.json();
}

export async function bulkRevokeAgents(
  tenantExternalId: string,
  filter: { identityExternalId?: string; scope?: string; tool?: string; all?: boolean }
): Promise<{ revoked: number; agents: string[] }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/agents/bulk-revoke`,
    { method: "POST", headers: headersJson(), body: JSON.stringify(filter) }
  );
  if (!res.ok) throw new Error(`bulk-revoke failed: ${res.status}`);
  return res.json();
}

// ── Tier 3 — control-framework coverage + SIEM export ────────────────────────

export interface ControlMapping {
  id?: string;
  capability: string;
  owaspAsi: string[];
  nist: string[];
  status: "enforced" | "partial" | "planned";
  evidence: string;
}

export interface ControlCoverageSummary {
  total: number;
  enforced: number;
  partial: number;
  planned: number;
  owaspAsiCovered: string[];
  nistConceptsCovered: string[];
}

export async function fetchControlCoverage(
  tenantExternalId: string
): Promise<{ coverage: ControlCoverageSummary; controls: ControlMapping[] }> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/agents/control-coverage`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`control-coverage failed: ${res.status}`);
  return res.json();
}

export async function fetchSiemExport(
  tenantExternalId: string,
  format: "ndjson" | "cef" = "ndjson"
): Promise<string> {
  const res = await fetch(
    `${API_BASE}/tenants/${encodeURIComponent(tenantExternalId)}/agents/siem-export?format=${format}`,
    { headers: headersJson() }
  );
  if (!res.ok) throw new Error(`siem-export failed: ${res.status}`);
  return res.text();
}

// ── Agent delegation chains + shadow discovery + anomalies ───────────────────

export interface DelegationChainRow {
  chainId: string;
  originatorId: string;
  currentAgentId: string;
  effectiveScopes: string[];
  effectiveResources: string[];
  depth: number;
  maxDepth: number;
  status: string;
  expiresAt: string;
  createdAt: string;
}

export interface DelegationLinkRow {
  linkId: string;
  chainId: string;
  linkIndex: number;
  delegatorId: string;
  delegateeId: string;
  delegatedScopes: string[];
  delegatedResources: string[];
  expiresAt: string;
  createdAt: string;
}

export async function fetchDelegationChains(
  tenantExternalId: string,
  opts: { agentId?: string } = {}
): Promise<{ chains: DelegationChainRow[] }> {
  const q = opts.agentId ? `?agentId=${encodeURIComponent(opts.agentId)}` : "";
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/agents/delegation-chains${q}`
  );
  if (!res.ok) throw new Error(`delegation chains failed: ${res.status}`);
  return res.json();
}

export async function fetchDelegationChainStats(
  tenantExternalId: string
): Promise<{ stats: { total: number; byStatus: Record<string, number>; averageDepth: number; maxDepth: number } }> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/agents/delegation-chains/stats`
  );
  if (!res.ok) throw new Error(`delegation stats failed: ${res.status}`);
  return res.json();
}

export async function fetchDelegationChain(
  tenantExternalId: string,
  chainId: string
): Promise<{ chain: DelegationChainRow; links: DelegationLinkRow[] }> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/agents/delegation-chains/${encodeURIComponent(chainId)}`
  );
  if (!res.ok) throw new Error(`delegation chain failed: ${res.status}`);
  return res.json();
}

export async function createDelegationChain(
  tenantExternalId: string,
  body: { delegateeId: string; scopes: string[]; resources?: string[]; expiresInMs: number; maxDepth?: number }
): Promise<DelegationChainRow> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/agents/delegation-chains`,
    { method: "POST", body: JSON.stringify(body) }
  );
  if (!res.ok) throw new Error(`create chain failed: ${res.status}`);
  return res.json();
}

export async function extendDelegationChain(
  tenantExternalId: string,
  chainId: string,
  body: { delegatorId: string; delegateeId: string; scopes: string[]; resources?: string[]; expiresInMs: number }
): Promise<{ chainId: string; depth: number; currentAgentId: string; status: string }> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/agents/delegation-chains/${encodeURIComponent(chainId)}/extend`,
    { method: "POST", body: JSON.stringify(body) }
  );
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new Error((detail as { error?: string }).error ?? `extend chain failed: ${res.status}`);
  }
  return res.json();
}

export async function revokeDelegationChain(
  tenantExternalId: string,
  chainId: string,
  reason: string
): Promise<{ chainId: string; status: string }> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/agents/delegation-chains/${encodeURIComponent(chainId)}/revoke`,
    { method: "POST", body: JSON.stringify({ reason }) }
  );
  if (!res.ok) throw new Error(`revoke chain failed: ${res.status}`);
  return res.json();
}

// ── Agent sessions (Phase 5 — task-bound MCP tokens) ─────────────────────────

export interface AgentSessionRow {
  tokenId: string;
  sessionId: string;
  intentId: string | null;
  declaredIntent: string | null;
  taskId: string | null;
  chainId: string | null;
  originator: string | null;
  scopes: string[];
  issuedAt: string;
  expiresAt: string;
}

export async function fetchAgentSessions(
  tenantExternalId: string
): Promise<{ sessions: AgentSessionRow[] }> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/agent-sessions`
  );
  if (!res.ok) throw new Error(`agent sessions failed: ${res.status}`);
  return res.json();
}

export async function completeAgentTask(
  tenantExternalId: string,
  taskId: string,
  reason = "operator_terminated"
): Promise<{ taskId: string; status: string; tokensRevoked: number }> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/agent-tasks/${encodeURIComponent(taskId)}/complete`,
    { method: "POST", body: JSON.stringify({ reason }) }
  );
  if (!res.ok) throw new Error(`complete task failed: ${res.status}`);
  return res.json();
}

export interface AgentDiscoveryCandidateRow {
  fingerprint: string;
  suggestedName: string | null;
  framework: string;
  discoveryMethod: string;
  confidence: string;
  isShadowAgent: boolean;
  shadowRiskScore: number;
  detectedCapabilities?: string[];
  detectedResources?: string[];
  riskIndicators?: string[];
  status: string;
  firstSeen: string;
  lastSeen: string;
}

export async function fetchAgentDiscoveryCandidates(
  tenantExternalId: string,
  status?: string
): Promise<{ candidates: AgentDiscoveryCandidateRow[] }> {
  const q = status ? `?status=${encodeURIComponent(status)}` : "";
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/agents/discovery-candidates${q}`
  );
  if (!res.ok) throw new Error(`discovery candidates failed: ${res.status}`);
  return res.json();
}

export async function triageAgentDiscoveryCandidate(
  tenantExternalId: string,
  fingerprint: string,
  action: "promote" | "dismiss"
): Promise<{ success: boolean; status: string }> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/agents/discovery-candidates/${encodeURIComponent(fingerprint)}/${action}`,
    { method: "POST" }
  );
  if (!res.ok) throw new Error(`triage candidate failed: ${res.status}`);
  return res.json();
}

/** Triggers a shadow-agent discovery run (LLM keys + OAuth grants + MCP). */
export async function runShadowAgentDiscovery(
  tenantExternalId: string
): Promise<{
  candidates: number;
  nhis: number;
  mcpServers: number;
  errors: Array<{ source: string; error: string }>;
}> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/agents/shadow-discovery/run`,
    { method: "POST" }
  );
  if (!res.ok) throw new Error(`shadow discovery run failed: ${res.status}`);
  return res.json();
}

export interface McpServerRow {
  serverUrl: string;
  name: string | null;
  transport: string;
  authType: string;
  discoveredVia: string;
  toolCount: number;
  tools: string[];
  riskIndicators: string[];
  shadowRiskScore: number;
  isGoverned: boolean;
  status: string;
  /** Whether a sealed upstream credential is stored for MCP gateway injection. */
  hasCredential: boolean;
  firstSeen: string;
  lastSeen: string;
}

export async function fetchMcpServers(
  tenantExternalId: string,
  status?: string
): Promise<{ servers: McpServerRow[] }> {
  const q = status ? `?status=${encodeURIComponent(status)}` : "";
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/agents/mcp-servers${q}`
  );
  if (!res.ok) throw new Error(`mcp servers failed: ${res.status}`);
  return res.json();
}

export async function triageMcpServer(
  tenantExternalId: string,
  serverUrl: string,
  action: "trust" | "dismiss"
): Promise<{ success: boolean; status: string }> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/agents/mcp-servers/${action}`,
    { method: "POST", body: JSON.stringify({ serverUrl }) }
  );
  if (!res.ok) throw new Error(`triage mcp server failed: ${res.status}`);
  return res.json();
}

/**
 * Seals (or clears, when `credential` is empty) the upstream credential the MCP
 * gateway proxy injects for a server. The plaintext is KMS-sealed server-side and
 * never returned.
 */
export async function setMcpServerCredential(
  tenantExternalId: string,
  input: { serverUrl: string; header?: string; valuePrefix?: string; credential: string }
): Promise<{ success: boolean; hasCredential: boolean }> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/agents/mcp-servers/credential`,
    { method: "POST", body: JSON.stringify(input) }
  );
  if (!res.ok) throw new Error(`set mcp server credential failed: ${res.status}`);
  return res.json();
}

export interface AgentPolicy {
  challengeThreshold?: number;
  revokeThreshold?: number;
  defaultAllowedTools?: string[];
  defaultRequireApprovalTools?: string[];
  /** When `enforce`, a PEP deny revokes tokens and suspends the agent identity. */
  authorizeEnforcement?: "advisory" | "enforce";
  /** When true, `/authorize` requires a valid active delegation chain (fail closed). */
  requireDelegationChain?: boolean;
}

export async function fetchAgentPolicy(tenantExternalId: string): Promise<{ policy: AgentPolicy }> {
  const res = await governanceApiFetch(`/tenants/${encodeURIComponent(tenantExternalId)}/agents/policy`);
  if (!res.ok) throw new Error(`agent policy load failed: ${res.status}`);
  return res.json();
}

export async function saveAgentPolicy(
  tenantExternalId: string,
  policy: AgentPolicy
): Promise<{ policy: AgentPolicy }> {
  const res = await governanceApiFetch(`/tenants/${encodeURIComponent(tenantExternalId)}/agents/policy`, {
    method: "PUT",
    body: JSON.stringify(policy),
  });
  if (!res.ok) throw new Error(`agent policy save failed: ${res.status}`);
  return res.json();
}

export interface AgentAnomalyEventRow {
  agentId: string;
  anomalyScore: number;
  signals: string[];
  actionCount: number;
  occurredAt: string;
}

export async function fetchAgentAnomalyEvents(
  tenantExternalId: string,
  agentId?: string
): Promise<{ events: AgentAnomalyEventRow[] }> {
  const q = agentId ? `?agentId=${encodeURIComponent(agentId)}` : "";
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/agents/anomaly-events${q}`
  );
  if (!res.ok) throw new Error(`anomaly events failed: ${res.status}`);
  return res.json();
}

export type ApplicationIntegrationMode = "ephemeral" | "caep_ssf" | "idp_backchannel";
export type ApplicationSensitivity = "low" | "medium" | "high" | "critical";
export type ApplicationStatus = "draft" | "active" | "disabled";
export type ApplicationProtocol = "oidc" | "saml";
export type PrerequisiteAudience = "operator" | "engineer";
export type PrerequisiteProvider = "auth0" | "okta" | "entra" | "oidc";
export type AppRiskLevel = "low" | "medium" | "high" | "critical";

export interface RevocationChannelSpec {
  channel: string;
  latencyClass: "sub_second" | "seconds" | "token_expiry";
  description: string;
  requires?: string;
}

export interface ProviderCoverage {
  provider: string;
  label: string;
  capture: boolean;
  outboundKill: boolean;
  notes?: string;
  revocationChannels?: RevocationChannelSpec[];
  typicalLatency?: string;
}

export interface ModeGuidance {
  mode: ApplicationIntegrationMode;
  label: string;
  bestFor: string;
  requires: string;
}

export interface CoverageMatrix {
  providers: ProviderCoverage[];
  modes: ModeGuidance[];
}

export interface ProtectedAppTemplate {
  id: string;
  name: string;
  category: string;
  suggestedMode: ApplicationIntegrationMode;
  appClientIdHint: string;
  notes: string;
  suggestedSensitivity?: ApplicationSensitivity;
}

export interface AppRiskAssessment {
  score: number;
  level: AppRiskLevel;
  reasons: string[];
}

export type AppWarningCode =
  | "disabled_but_linked"
  | "enabled_not_configured"
  | "degraded_protection"
  | "missing_required_link"
  | "orphaned_receiver"
  | "orphaned_idp_connection";

export interface AppWarning {
  code: AppWarningCode;
  severity: "info" | "warning" | "critical";
  message: string;
  remediation: string;
}

/**
 * RFC-003 — best-known downstream enforcement tier for an app. `pep` is the
 * strongest (a policy enforcement point validates inbound tokens in < 2s off
 * the revocation feed); `ttl_bounded` merely waits out the IdP token lifetime.
 */
export type ApplicationEnforcementTier =
  | "pep"
  | "sidecar"
  | "idp_signal"
  | "ttl_bounded"
  | "none";

export interface ProtectedApplicationRow {
  externalId: string;
  name: string;
  appClientId: string;
  integrationMode: ApplicationIntegrationMode;
  idpConnectionExternalId: string | null;
  ssfReceiverId: string | null;
  config: Record<string, unknown>;
  sensitivity: ApplicationSensitivity | null;
  status: ApplicationStatus;
  protocol: ApplicationProtocol;
  privileged: boolean;
  /** RFC-003 — best-known downstream enforcement tier (null = unknown). */
  enforcementTier: ApplicationEnforcementTier | null;
  /** RFC-003 — last time a PEP confirmed enforcement (ISO-8601), if ever. */
  lastPepSeenAt: string | null;
  enabled: boolean;
  risk: AppRiskAssessment;
  warnings: AppWarning[];
  createdAt: string;
  updatedAt: string;
}

export interface ProtectedAppReadiness {
  appExternalId: string;
  appClientId: string;
  integrationMode: ApplicationIntegrationMode;
  enabled: boolean;
  overall: "ready" | "degraded" | "not_configured";
  /**
   * RFC-003 B2 — honesty-derived enforcement tier. `ttl_bounded` here means the
   * linked IdP has no instant revoke path and no PEP/sidecar/instant channel is
   * confirmed, so a revoked session lingers until its token TTL expires.
   */
  derivedEnforcementTier?: ApplicationEnforcementTier | null;
  prerequisites: Array<{
    id: string;
    label: string;
    detail: string;
    where: string;
    when: string;
    how: string;
    providerConsole?: Partial<Record<PrerequisiteProvider, string>>;
    audience: PrerequisiteAudience;
    optional?: boolean;
    satisfied: boolean;
  }>;
  signals: Record<string, string | boolean | number | null>;
  risk?: AppRiskAssessment;
  warnings?: AppWarning[];
  sessionRisk?: {
    activeSessions: number;
    peakRiskStatus?: "low" | "medium" | "high" | "critical";
  } | null;
}

export interface ProtectedAppValidationResult {
  ok: boolean;
  issues: Array<{ field: string; message: string }>;
  warnings: AppWarning[];
}

export type DeleteProtectedApplicationResult =
  | { deleted: true }
  | { deleted: false; conflict: { lastSessionCreatedAt: string | null; hint: string } };

export async function fetchProtectedApplications(
  tenantExternalId: string
): Promise<{ items: ProtectedApplicationRow[]; total: number }> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/applications`
  );
  if (!res.ok) throw new Error(`applications list failed: ${res.status}`);
  return res.json();
}

export async function createProtectedApplication(
  tenantExternalId: string,
  body: {
    name: string;
    appClientId: string;
    integrationMode: ApplicationIntegrationMode;
    idpConnectionExternalId?: string | null;
    ssfReceiverId?: string | null;
    config?: Record<string, unknown>;
    sensitivity?: ApplicationSensitivity | null;
    status?: ApplicationStatus;
    protocol?: ApplicationProtocol;
    privileged?: boolean;
    enabled?: boolean;
  }
): Promise<ProtectedApplicationRow> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/applications`,
    { method: "POST", body: JSON.stringify(body) }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? `create application failed: ${res.status}`);
  }
  return res.json();
}

export async function updateProtectedApplication(
  tenantExternalId: string,
  appExternalId: string,
  body: Partial<{
    name: string;
    appClientId: string;
    integrationMode: ApplicationIntegrationMode;
    idpConnectionExternalId: string | null;
    ssfReceiverId: string | null;
    config: Record<string, unknown>;
    sensitivity: ApplicationSensitivity | null;
    status: ApplicationStatus;
    privileged: boolean;
    enabled: boolean;
  }>
): Promise<ProtectedApplicationRow> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/applications/${encodeURIComponent(appExternalId)}`,
    { method: "PATCH", body: JSON.stringify(body) }
  );
  if (!res.ok) throw new Error(`update application failed: ${res.status}`);
  return res.json();
}

/**
 * Soft-deletes a protected application. Without `force`, the server returns 409 if
 * the app protected a session recently; the caller can re-invoke with force=true.
 */
export async function deleteProtectedApplication(
  tenantExternalId: string,
  appExternalId: string,
  opts?: { force?: boolean }
): Promise<DeleteProtectedApplicationResult> {
  const q = opts?.force ? "?force=true" : "";
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/applications/${encodeURIComponent(appExternalId)}${q}`,
    { method: "DELETE" }
  );
  if (res.ok || res.status === 204) return { deleted: true };
  if (res.status === 409) {
    const body = (await res.json().catch(() => ({}))) as {
      dependency?: { lastSessionCreatedAt?: string | null };
      hint?: string;
    };
    return {
      deleted: false,
      conflict: {
        lastSessionCreatedAt: body.dependency?.lastSessionCreatedAt ?? null,
        hint: body.hint ?? "This application has recent session activity.",
      },
    };
  }
  throw new Error(`delete application failed: ${res.status}`);
}

/** Pre-save validation: checks a candidate config without persisting. */
export async function validateProtectedApplication(
  tenantExternalId: string,
  candidate: Partial<{
    externalId: string;
    name: string;
    appClientId: string;
    integrationMode: ApplicationIntegrationMode;
    idpConnectionExternalId: string | null;
    ssfReceiverId: string | null;
    sensitivity: ApplicationSensitivity | null;
    protocol: ApplicationProtocol;
    enabled: boolean;
  }>
): Promise<ProtectedAppValidationResult> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/applications/validate`,
    { method: "POST", body: JSON.stringify(candidate) }
  );
  if (!res.ok) throw new Error(`validate application failed: ${res.status}`);
  return res.json();
}

export async function fetchProtectedAppReadiness(
  tenantExternalId: string,
  appExternalId: string
): Promise<ProtectedAppReadiness> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/applications/${encodeURIComponent(appExternalId)}/readiness`
  );
  if (!res.ok) throw new Error(`readiness failed: ${res.status}`);
  return res.json();
}

export async function fetchCoverageMatrix(tenantExternalId: string): Promise<CoverageMatrix> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/applications/coverage-matrix`
  );
  if (!res.ok) throw new Error(`coverage matrix failed: ${res.status}`);
  return res.json();
}

export interface IdpPostureCheck {
  id: string;
  label: string;
  status: "pass" | "warn" | "fail" | "unknown";
  detail?: string;
  remediation?: string;
  remediable?: boolean;
}

export interface IdpCredentialPosture {
  appExternalId: string;
  appClientId: string;
  provider: string | null;
  checkedAt: string;
  checks: IdpPostureCheck[];
  instantChannelReady: boolean;
  shortTokenLifetimeVerified: boolean;
  /** IA's public BCL relay URL to register in the IdP for intranet apps. */
  bclRelayUrl?: string | null;
}

export async function fetchIdpPosture(
  tenantExternalId: string,
  appExternalId: string
): Promise<IdpCredentialPosture> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/applications/${encodeURIComponent(appExternalId)}/idp-posture`
  );
  if (!res.ok) throw new Error(`idp posture failed: ${res.status}`);
  return res.json();
}

export async function remediateIdpPosture(
  tenantExternalId: string,
  appExternalId: string,
  checkId: string
): Promise<{ ok: boolean; message: string }> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/applications/${encodeURIComponent(appExternalId)}/idp-posture/remediate`,
    { method: "POST", body: JSON.stringify({ checkId }) }
  );
  if (!res.ok && res.status !== 422) throw new Error(`posture remediation failed: ${res.status}`);
  return res.json();
}

export async function fetchProtectedAppTemplates(
  tenantExternalId: string
): Promise<{ items: ProtectedAppTemplate[] }> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/applications/templates`
  );
  if (!res.ok) throw new Error(`templates failed: ${res.status}`);
  return res.json();
}

export interface RevokeDryRunResult {
  appExternalId: string;
  executed: false;
  fallbackLegacy: boolean;
  readiness: "ready" | "degraded" | "not_configured";
  wouldRun: string[];
  plan: {
    vaultRevoke: boolean;
    idpKill: boolean;
    scopedCaepReceiverId: string | null;
    globalCaepFanout: boolean;
    idpConnectionExternalId: string | null;
  };
}

export type PamGrantStatus = "pending" | "active" | "denied" | "revoked" | "expired";
export type PamProvisioning = "token" | "lease" | "both";
export type PamRevokedReason = "operator" | "expired" | "risk_triggered" | "provision_failed";

export interface PamGrant {
  externalId: string;
  protectedAppExternalId: string;
  subject: string;
  justification: string | null;
  status: PamGrantStatus;
  ttlSeconds: number;
  provisioning: PamProvisioning;
  requestedBy: string;
  approvedBy: string | null;
  requestedAt: string;
  decidedAt: string | null;
  expiresAt: string | null;
  /** Ephemeral IA session id minted on approve (token/both). */
  sessionId: string | null;
  /** jit-secret-provisioner lease id (lease/both). */
  leaseExternalId: string | null;
  provisionedAt: string | null;
  revokedReason: PamRevokedReason | null;
}

/** Result of a PAM call — distinguishes the feature being disabled from other errors. */
export type PamResult<T> = { ok: true; value: T } | { ok: false; disabled: boolean; error: string };

async function pamFetch<T>(path: string, init?: RequestInit): Promise<PamResult<T>> {
  const res = await governanceApiFetch(path, init);
  if (res.ok) return { ok: true, value: (await res.json()) as T };
  const body = (await res.json().catch(() => ({}))) as {
    error?: string;
    code?: string;
    hint?: string;
    message?: string;
  };
  // Map the backend's stable error codes to operator-friendly copy. A
  // multi-approver grant can't be single-approved — point at the quorum vote.
  const friendly =
    body.error === "multi_approver_grant"
      ? body.hint ??
        "This grant requires multiple approvers — record a quorum vote instead of a single approval."
      : body.error === "grant_not_pending"
        ? body.message ?? "This grant can no longer be actioned in its current state."
        : body.message ?? body.error ?? `request failed: ${res.status}`;
  return {
    ok: false,
    disabled: res.status === 403 && body.code === "pam_disabled",
    error: friendly,
  };
}

export function fetchPamGrants(
  tenantExternalId: string,
  appExternalId: string,
  status?: PamGrantStatus
): Promise<PamResult<{ items: PamGrant[] }>> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  return pamFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/applications/${encodeURIComponent(appExternalId)}/pam-grants${qs}`
  );
}

/** Tenant-wide pending-approvals list (approver view) across all privileged apps. */
export function fetchTenantPamGrants(
  tenantExternalId: string,
  status?: PamGrantStatus
): Promise<PamResult<{ items: PamGrant[] }>> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  return pamFetch(`/tenants/${encodeURIComponent(tenantExternalId)}/pam-grants${qs}`);
}

export function requestPamGrant(
  tenantExternalId: string,
  appExternalId: string,
  body: {
    subject: string;
    ttlSeconds: number;
    justification?: string;
    provisioning?: PamProvisioning;
  }
): Promise<PamResult<PamGrant>> {
  return pamFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/applications/${encodeURIComponent(appExternalId)}/pam-grants`,
    { method: "POST", body: JSON.stringify(body) }
  );
}

export function decidePamGrant(
  tenantExternalId: string,
  grantExternalId: string,
  action: "approve" | "deny" | "revoke"
): Promise<PamResult<PamGrant>> {
  return pamFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/pam-grants/${encodeURIComponent(grantExternalId)}/${action}`,
    { method: "POST", body: JSON.stringify({}) }
  );
}

/** Read-only: plans the teardown cascade without executing any revocation. */
export async function revokeDryRun(
  tenantExternalId: string,
  appExternalId: string
): Promise<RevokeDryRunResult> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/applications/${encodeURIComponent(appExternalId)}/revoke-dry-run`,
    { method: "POST" }
  );
  if (!res.ok) throw new Error(`dry-run failed: ${res.status}`);
  return res.json();
}

// ─── Predictive Session Pruning (Phase 6) ─────────────────────────────────────

export type PredictivePruningAggressiveness = "conservative" | "moderate" | "aggressive";

export interface PredictivePruningPolicyResponse {
  tenantExternalId: string;
  source: "tenant" | "default";
  policy: {
    enabled: boolean;
    aggressiveness: PredictivePruningAggressiveness;
  };
}

export async function fetchPredictivePruningConfig(
  tenantExternalId: string
): Promise<PredictivePruningPolicyResponse> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/predictive-pruning/config`
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `predictive-pruning config failed: ${res.status}`);
  }
  return res.json() as Promise<PredictivePruningPolicyResponse>;
}

export async function savePredictivePruningConfig(
  tenantExternalId: string,
  policy: { enabled: boolean; aggressiveness: PredictivePruningAggressiveness }
): Promise<PredictivePruningPolicyResponse> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/predictive-pruning/config`,
    { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(policy) }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string; details?: unknown };
    throw new Error(err.message ?? `save predictive-pruning config failed: ${res.status}`);
  }
  return res.json() as Promise<PredictivePruningPolicyResponse>;
}

export async function resetPredictivePruningConfig(
  tenantExternalId: string
): Promise<PredictivePruningPolicyResponse> {
  const res = await governanceApiFetch(
    `/tenants/${encodeURIComponent(tenantExternalId)}/predictive-pruning/reset`,
    { method: "POST" }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `reset predictive-pruning config failed: ${res.status}`);
  }
  return res.json() as Promise<PredictivePruningPolicyResponse>;
}

